import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Handle,
  MarkerType,
  Position,
  ViewportPortal,
  type Edge,
  type Node,
  type NodeProps,
  type ReactFlowInstance,
} from "@xyflow/react";
import * as React from "react";
import "@xyflow/react/dist/style.css";
import { FloatingEdge } from "./FloatingEdge";
import { CascadeEdge } from "./CascadeEdge";
import { autoLayout } from "./layout";
import { cascadeKey, cascadeLayout, type CascadeLayout } from "./cascade";
import {
  pillExecutorOf,
  pillModelLine,
  pillPromptOf,
  promptAsSent,
  shortCondition,
  type PillPrompt,
} from "./runPill";
import { flowToRf, forEachChildrenToRf, artifactsToRf } from "./transforms";
import type { ArtifactNodeData } from "./transforms";
import type { FlowDefinition, NodeDefinition } from "./types";
import type { NodeTraceEntry } from "./runTrace";
import { filterActions, deriveFiles, MAX_ACTIONS_PER_NODE } from "./actionLog";
import type { ActionRecord, ActionKind } from "./actionLog";
import { RunLog, sumRunTokens, computeRunElapsedMs } from "./RunLog";
import { RunGraphs, type RunGraphKind } from "./RunGraphs";
import {
  DebugToolbar,
  PausedNodeCard,
  BudgetPills,
  budgetPills,
  type DebugVerb,
  type RunDebugState,
  type RunApprovalRequest,
} from "./RunDebugger";
import "./styles.css";

export type NodeRunStatus =
  | "pending"
  | "running"
  | "completed"
  | "failed"
  | "awaiting-approval"
  // Weave (Docit Work 15 §6.1): a node can also be waiting on a person, queued behind a parallelism
  // cap or a WIP lane, held by a human, or skipped. Drawing any of those as "pending" would say
  // "not reached yet" about work that is deliberately stopped — the lie the owner asked us to fix.
  | "waiting"
  | "queued"
  | "held"
  | "skipped";

/**
 * Weave — what a node IS to the department, drawn on the node card: the executor kind (who runs
 * it) and the tile it carries (key, kind, state, and the small details a reviewer reads at a
 * glance). Absent ⇒ the node draws exactly as it did before Weave, which is what keeps every
 * non-project flow unchanged.
 */
export interface NodeTileChip {
  key: string;
  kind: string;
  state: string;
  /** The cross-system state category — this is what COLOURS the chip, never the kind. */
  category: "proposed" | "committed" | "active" | "completed" | "removed" | string;
  title?: string;
  priority?: string | null;
  severity?: string | null;
  estimate?: number | null;
  blocked?: boolean;
  blockedReason?: string | null;
  held?: boolean;
  assignee?: { kind: string; id: string | null } | null;
  isRef?: boolean;
  role?: string;
}

/** The executor kinds the runner distinguishes (15 §6.1). */
export type NodeExecutorKind =
  | "thinker"
  | "human"
  | "compute"
  | "work"
  | "fork"
  | "join"
  | "foreach"
  | "subflow"
  | "mechanics";

/**
 * Everything the host learned about one node's execution — fed from the
 * executor's event stream (tool_start/usage/init events) and rendered in the
 * inspector panel. All fields optional: the panel shows what it has.
 */
export interface NodeRunMeta {
  /** Exact retained reasoning records produced by a server reasoning/1 node. */
  reasoningSessionId?: string;
  candidateRecordId?: string;
  evaluationRecordId?: string;
  acceptanceStatus?: string;
  /** Executing thinker id, e.g. "ide:claude". */
  thinkerId?: string;
  /** Human persona of the node ("NORA, a product manager"). */
  persona?: string;
  /** Model that served the run, e.g. "claude-sonnet-5". */
  model?: string;
  /** Where the flow/thinker came from, e.g. "remote flow · IDE thinker". */
  source?: string;
  /** Tool name → number of invocations. */
  tools?: Record<string, number>;
  /** Skills invoked (deduped, in first-use order). */
  skills?: string[];
  startedAt?: number;
  durationMs?: number;
  inputTokens?: number;
  outputTokens?: number;
  totalTokens?: number;
  /**
   * Turns the node consumed, as reported by the provider (`num_turns`). MEASURED — when it is
   * absent the used-vs-budget pill/graph is omitted rather than derived from action counts.
   */
  turnsUsed?: number;
  /**
   * Turns as the BUDGET counts them — assistant messages observed on the stream (flow-platform F2).
   *
   * Distinct from `turnsUsed` (the CLI's `num_turns`) and PREFERRED for any used-vs-budget display:
   * a live node rendered "15/11 exceeded" while the hard ceiling never fired, because the pill and
   * the enforcement were reading two different counts. Enforcement can only act on what it observes
   * mid-flight, so that is the number a budget reading must show. Absent ⇒ fall back to `turnsUsed`.
   */
  budgetTurns?: number;
  /** The node's declared `turnBudget`, so used-vs-budget has something to compare against. */
  turnBudgetSoft?: number;
  turnBudgetHard?: number;
  /**
   * Payload accounting (flow-data-plane W5). `promptChars` is what was carried INTO this node's
   * prompt; `artifactBytes` is what its artifacts hold on disk. Together they are the payload-cost
   * graph: a copy-based flow's promptChars climbs with the dataset, a handle-based flow's stays flat
   * while artifactBytes carries the size. Both MEASURED — promptChars from the prompt string the
   * node actually received, artifactBytes from the files that were actually written.
   */
  promptChars?: number;
  artifactBytes?: number;
  /**
   * Artifacts this node produced or was handed, for the pills. Path stays on the executor (Q6).
   * `dir`/`version`/`subscribed` (flow-shared-context R1/R3) record direction and the registry
   * version seen; absent on pre-0.5.42 runs → `artifactsToRf` falls back to its heuristic.
   */
  artifacts?: Array<{
    path: string;
    bytes: number;
    mediaType: string;
    rows?: number;
    written?: boolean;
    dir?: "produced" | "consumed";
    version?: number;
    subscribed?: boolean;
  }>;
  /** Set when the node was handed a handle and never read the file it points at (Q3). */
  handleUnread?: string[];
  /** Latest activity line while the node is running ("Read: src/foo.ts"). */
  lastEvent?: string;
  /** Transition assessment outcome: where the node sent the flow and why. */
  decision?: string;
  /** Artifacts this node created via directives ("task: …", "todo: …"). */
  created?: string[];
  /**
   * The prompt AS SENT to the executor (server step meta carries it; the IDE run has it as the
   * transcript's first `prompt` entry instead). The cascade pill's full-prompt view shows it
   * beside the authored words.
   */
  prompt?: string;
}

/**
 * One subflow CHILD RUN as the inset draws it (visualizer v4): flow definition for the
 * mini-graph, per-node statuses, active node, lifecycle. Mirrors the host's ChildRunState.
 */
export interface ChildRunState {
  childInstanceId: string;
  flowId: string;
  parentNodeId?: string;
  status: "running" | "completed" | "failed";
  flow?: FlowDefinition;
  statuses: Record<string, NodeRunStatus>;
  currentNodeId?: string | null;
  startedAt: number;
  /** v4.1: per-node measured work — the token spread inside the child. */
  metas?: Record<
    string,
    {
      model?: string;
      totalTokens?: number;
      turnsUsed?: number;
      durationMs?: number;
      startedAt?: number;
    }
  >;
  /** v4.1: per-node result text (host-capped) — the drill-in's content. */
  results?: Record<string, string>;
}

export interface FlowRunViewProps {
  /** The flow being run (drawn once; its saved layout is respected). */
  flow: FlowDefinition;
  /**
   * Open a spilled artifact by path (flow-platform F15). Present ⇒ artifact pills become buttons.
   *
   * Optional, and absent is meaningful: artifacts stay on the EXECUTOR's machine (an accepted
   * decision — uploading payloads would recreate the transport cost the data plane removed), so a
   * surface that cannot reach the file must not offer a button that silently fails.
   */
  onOpenArtifact?: (path: string) => void;
  /** Per-node run status, keyed by nodeId. Missing → "pending". */
  statuses?: Record<string, NodeRunStatus>;
  /**
   * Weave: the tile each node IS, keyed by nodeId (from `runs.tree`). Present ⇒ the node card
   * carries a coloured tile chip; absent ⇒ nothing changes.
   */
  tiles?: Record<string, NodeTileChip>;
  /** Weave: the executor kind per node (from `runs.tree`). Drives the node's glyph. */
  executors?: Record<string, NodeExecutorKind | string>;
  /** Weave: why a node is queued ("maxParallel 2/2", "wip thinker:x 2/2"), keyed by nodeId. */
  queuedReasons?: Record<string, string>;
  /** Weave: click a node's tile chip to open the tile. */
  onOpenTile?: (key: string) => void;
  /** The node currently executing — gets an extra glow. */
  currentNodeId?: string | null;
  /**
   * Wave 2 fork/join: EVERY currently-executing node (one per parallel branch).
   * When provided (non-empty) it supersedes `currentNodeId`; `current` becomes
   * set membership so N branches glow at once. Purely additive — hosts that
   * keep passing only `currentNodeId` behave exactly as before.
   */
  currentNodeIds?: string[] | null;
  /** Per-node execution details, keyed by nodeId — enables the inspector. */
  metas?: Record<string, NodeRunMeta>;
  /** Directed edges the executor actually traversed — highlighted green. */
  takenEdges?: Array<{ from: string; to: string }>;
  /**
   * Per-node run transcript (the chat window's stream, per node): prompt,
   * thinking, streamed content, tool lines, result, decision. When present,
   * the inspector renders a live transcript section for the selected node.
   */
  traces?: Record<string, NodeTraceEntry[]>;
  /**
   * Per-node structured action log (FR-1). Powers the inspector drill-down
   * (WU-2), the live bubble (WU-3), and the {@link RunLog} table + header
   * token/elapsed rollup (WU-4). Optional — omitting it just means an empty
   * log/bubble/drill-down; the web canvas keeps compiling and rendering
   * without it (NF-1).
   */
  actions?: Record<string, ActionRecord[]>;
  /**
   * Run-control state (flow-debugger-ui W2). Present ⇒ the debugger toolbar, breakpoint dots and
   * the held-node card render. Absent ⇒ this view is exactly the read-only run view it was, which
   * is what keeps every other host of FlowRunView (the KnowIt canvas, the editor preview)
   * unchanged.
   */
  debug?: RunDebugState | null;
  /** A `requireApproval` decision the panel is hosting, if any. */
  approval?: RunApprovalRequest | null;
  onDebugVerb?: (verb: DebugVerb, arg?: string) => void;
  onToggleBreakpoint?: (nodeId: string) => void;
  onApprovalDecision?: (nodeId: string, approved: boolean) => void;
  /**
   * Which graphs and pills the host wants (W3 config: `docit.flowDebugger.graphs` /
   * `docit.flowDebugger.pills`). Omitted ⇒ all of them.
   */
  graphKinds?: ReadonlyArray<RunGraphKind>;
  pillKinds?: ReadonlyArray<string>;
  /**
   * Inspector/paused-card sidebar width in px (flow-visualizer-v3 R3), clamped to
   * [{@link RUN_PANEL_MIN_WIDTH}, {@link RUN_PANEL_MAX_WIDTH}]. Omitted or invalid ⇒
   * {@link RUN_PANEL_DEFAULT_WIDTH} — the same 360px the panel was hardcoded to before R3.
   */
  panelWidth?: number;
  /**
   * Fired once a drag ends with the clamped width, so a host that persists it (workspaceState,
   * localStorage, …) can restore the same width next time. Not fired on every drag frame.
   */
  onPanelWidthChange?: (width: number) => void;
  /**
   * Request the host read a small artifact's contents (flow-visualizer-v3 W2b `read_artifact` →
   * `artifact_content`, the simplify/v1 drill-in). Absent ⇒ the artifact inspector shows path +
   * Open only, same as F15's `onOpenArtifact` being absent.
   */
  onReadArtifact?: (path: string) => void;
  /** Results of `onReadArtifact` calls, keyed by artifact path. Missing ⇒ not yet requested. */
  artifactContents?: Record<string, ArtifactContentEntry>;
  /**
   * CURRENT artifact registry version per path (flow-shared-context R3, host `artifact_versions`
   * message). Drives stale consumer/subscription edges + the artifact node's version chip.
   */
  artifactVersions?: Record<string, number>;
  /** Subflow child runs, keyed by child instance id (visualizer v4 inset). */
  childRuns?: Record<string, ChildRunState>;
  /**
   * How many times each node has been attempted (retries count), keyed by nodeId — the cascade
   * pill shows it. Absent ⇒ no attempt count is drawn (never guessed from statuses).
   */
  attempts?: Record<string, number>;
  /**
   * The layout the canvas opens in. Omitted ⇒ the viewer's last choice (per browser), else
   * `"cascade"`. `"classic"` is the former auto-arrange (dagre, diagonal); `"saved"` is the flow's
   * own saved layout.
   */
  defaultLayout?: RunLayoutMode;
}

/** The run canvas's layouts: the left→right cascade of pills, the compact dagre graph, the saved one. */
export type RunLayoutMode = "cascade" | "classic" | "saved";

/**
 * One artifact's `read_artifact` round trip (flow-visualizer-v3 W2b). `unavailable` covers every
 * reason the host declined — not `.json`, over the size cap, unreadable, unparseable — collapsed to
 * one status because the inspector's response is the same for all of them (path + Open button,
 * never an error wall): the reason is kept only for a developer reading the host log, not surfaced.
 */
export interface ArtifactContentEntry {
  status: "loading" | "loaded" | "unavailable";
  json?: unknown;
  /** Raw text content, for artifacts that are not JSON (flow-shared-context: content-on-click). */
  text?: string;
  reason?: string;
  /** True when the content came from the KnowIt mirror, not this machine's disk. */
  remote?: boolean;
  /** Mirror metadata, present on remote reads — the audit facts worth showing. */
  version?: number;
  producedBy?: string;
}

interface RunNodeData {
  label: string;
  ownerRole: string;
  status: NodeRunStatus;
  current: boolean;
  /** A breakpoint is set on this node — the run stops BEFORE it executes. */
  breakpoint?: boolean;
  /** The run is held at this node right now. */
  heldHere?: boolean;
  /** Present ⇒ the dot is interactive (the host can toggle breakpoints). */
  onToggleBreakpoint?: (nodeId: string) => void;
  nodeId?: string;
  /** Set on a materialized forEach child (flow-visualizer-v3 R1) — its batch index, rendered as a "#k" badge. */
  childIndex?: number;
  /** Set on a materialized forEach child — the flow-definition nodeId it ran under (its own id has no `flow.nodes[]` entry). */
  templateNodeId?: string;
  // ── Visualizer v4: the bubble carries the work, not just a name ──
  model?: string;
  durationMs?: number;
  startedAt?: number;
  totalTokens?: number;
  turnsUsed?: number;
  budgetTurns?: number;
  artifactCount?: number;
  lastEvent?: string;
  /** Set on a subflow spawn node: its children's live tally. */
  subflowSummary?: { running: number; done: number; failed: number; total: number };
  // ── Weave (15 §6.1): the node's type and the tile it is ──
  executor?: NodeExecutorKind | string;
  tile?: NodeTileChip;
  queuedReason?: string;
  onOpenTile?: (key: string) => void;
  // ── Cascade pill (cascade.ts / runPill.ts): who runs it, on what, and the words it runs on ──
  executorLabel?: string;
  executorRole?: string;
  executorTitle?: string;
  modelLine?: string;
  prompt?: PillPrompt;
  attempts?: number;
  /** Opens the full-prompt viewer for this node. */
  onShowPrompt?: (nodeId: string) => void;
  [key: string]: unknown;
}

const STATUS_LABEL: Record<NodeRunStatus, string> = {
  pending: "",
  running: "running…",
  completed: "done",
  failed: "failed",
  "awaiting-approval": "approval",
  waiting: "waiting on a person",
  queued: "queued",
  held: "held",
  skipped: "skipped",
};

/**
 * One glyph per executor kind — the owner's "I want to see in the flow runner the type of node".
 * Text glyphs, not emoji: they inherit the theme's colour and never render as a coloured sticker
 * in a dark palette.
 */
const EXECUTOR_GLYPH: Record<string, string> = {
  thinker: "◈",
  human: "☗",
  compute: "⚙",
  work: "▦",
  fork: "⑂",
  join: "⑃",
  foreach: "↻",
  subflow: "⤷",
  mechanics: "○",
};

const EXECUTOR_TITLE: Record<string, string> = {
  thinker: "a thinker (AI) runs this node",
  human: "a person answers this node",
  compute: "a deterministic transform — no model",
  work: "a work operation on tiles — no model",
  fork: "fork: parallel branches",
  join: "join: the barrier",
  foreach: "forEach: one branch per element",
  subflow: "subflow: a child run",
  mechanics: "mechanics: this node carries no tile",
};

/** A tile's kind, as one glyph. Kind is a GLYPH; colour means state category (15 §6.2). */
const TILE_GLYPH: Record<string, string> = {
  epic: "◆",
  feature: "▣",
  story: "▤",
  task: "▢",
  bug: "✕",
  issue: "◇",
  question: "?",
  spike: "△",
  incident: "⚠",
};

/** Weave: the tile chip a node card carries — shared by the classic node and the cascade pill. */
function TileChip({ tile, onOpenTile }: { tile: NodeTileChip; onOpenTile?: (key: string) => void }) {
  return (
    <button
      type="button"
      className={`fn-node-tile cat-${tile.category}${tile.blocked ? " blocked" : ""}${tile.held ? " held" : ""}`}
      title={
        `${tile.key} · ${tile.kind} · ${tile.state}` +
        (tile.title ? `\n${tile.title}` : "") +
        (tile.blocked
          ? `\nblocked: ${tile.blockedReason ?? "waiting on a dependency"}`
          : "") +
        (tile.held ? "\nheld by a person" : "") +
        (tile.isRef ? "\n(an existing tile this node picked up)" : "")
      }
      onClick={(e) => {
        e.stopPropagation();
        if (onOpenTile) onOpenTile(tile.key);
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <span className="fn-node-tile-glyph">{TILE_GLYPH[tile.kind] ?? "▢"}</span>
      <span className="fn-node-tile-key">{tile.key}</span>
      <span className="fn-node-tile-state">{tile.state.replace(/_/g, " ")}</span>
      {tile.blocked ? (
        <span className="fn-node-tile-flag" title="blocked">
          ⛔
        </span>
      ) : null}
      {tile.held ? (
        <span className="fn-node-tile-flag" title="held">
          ⏸
        </span>
      ) : null}
      {tile.severity ? <span className="fn-node-tile-sev">{tile.severity}</span> : null}
      {tile.estimate != null ? (
        <span className="fn-node-tile-est">{tile.estimate}p</span>
      ) : null}
    </button>
  );
}

// Read-only node renderer: same shape as the editor's node, but coloured by
// run status (via the .status-* / .current classes in styles.css).
function RunNodeView({ data }: NodeProps) {
  const d = data as RunNodeData;
  const statusText = STATUS_LABEL[d.status] || "";
  const canBreak = !!d.onToggleBreakpoint && !!d.nodeId;
  return (
    <div
      className={`fn-node fn-run-node status-${d.status} ${
        d.current ? "current" : ""
      }${d.breakpoint ? " has-breakpoint" : ""}${d.heldHere ? " held" : ""}`}
    >
      <Handle type="target" position={Position.Top} className="fn-handle" />
      {canBreak ? (
        // Click toggles the breakpoint without pinning the inspector — the node's own click
        // handler must not also fire, or every breakpoint toggle would move the inspector.
        <button
          type="button"
          className={`fn-run-bp${d.breakpoint ? " on" : ""}`}
          title={d.breakpoint ? "Remove breakpoint" : "Break before this node"}
          aria-label={d.breakpoint ? `Remove breakpoint on ${d.label}` : `Break before ${d.label}`}
          aria-pressed={!!d.breakpoint}
          onClick={(e) => {
            e.stopPropagation();
            d.onToggleBreakpoint?.(String(d.nodeId));
          }}
          onMouseDown={(e) => e.stopPropagation()}
        />
      ) : null}
      {d.heldHere ? (
        <span className="fn-run-held-flag" title="the run is held here">
          ⏸
        </span>
      ) : null}
      <div className="fn-node-label">
        {d.childIndex != null ? (
          <span className="fn-run-child-badge" title={`batch #${d.childIndex}`}>
            #{d.childIndex}
          </span>
        ) : null}
        {d.executor ? (
          <span
            className={`fn-node-executor exec-${d.executor}`}
            title={EXECUTOR_TITLE[d.executor] ?? String(d.executor)}
            aria-label={EXECUTOR_TITLE[d.executor] ?? String(d.executor)}
          >
            {EXECUTOR_GLYPH[d.executor] ?? "◈"}
          </span>
        ) : null}
        {d.label}
      </div>
      {/* Weave: the tile this node IS. Colour = state category; the kind is the glyph. */}
      {d.tile ? <TileChip tile={d.tile} onOpenTile={d.onOpenTile} /> : null}
      {d.queuedReason ? (
        <div className="fn-node-queued" title={`queued: ${d.queuedReason}`}>
          queued · {d.queuedReason}
        </div>
      ) : null}
      <div className="fn-node-meta">
        <span className="fn-node-role">
          {d.tile?.assignee && d.tile.assignee.kind !== "none"
            ? `${d.tile.assignee.kind}: ${d.tile.assignee.id ?? ""}`
            : d.ownerRole || "no role"}
        </span>
        {statusText ? <span className="fn-run-status">{statusText}</span> : null}
        {d.subflowSummary ? (
          <span
            className={`fn-node-subflow-badge${d.subflowSummary.running > 0 ? " live" : ""}`}
            title={
              `${d.subflowSummary.running} running · ${d.subflowSummary.done} done` +
              (d.subflowSummary.failed ? ` · ${d.subflowSummary.failed} failed` : "") +
              " — click to open the subflow box"
            }
          >
            {d.subflowSummary.running > 0 ? "▶ " : ""}
            {d.subflowSummary.done + d.subflowSummary.failed}/{d.subflowSummary.total}
          </span>
        ) : null}
      </div>
      {/* v4: the work itself, in the bubble — measured facts, compact, only when present. */}
      {d.status === "running" && d.lastEvent ? (
        <div className="fn-node-live" title={d.lastEvent}>
          {d.lastEvent}
        </div>
      ) : null}
      {d.model ||
      d.durationMs != null ||
      d.totalTokens != null ||
      d.turnsUsed != null ||
      d.artifactCount ? (
        <div className="fn-node-stats">
          {d.model ? <span title={d.model}>{shortModelName(d.model)}</span> : null}
          {d.durationMs != null ? (
            <span>{fmtDurShort(d.durationMs)}</span>
          ) : d.status === "running" && d.startedAt ? (
            <span className="live">{fmtDurShort(Date.now() - d.startedAt)}…</span>
          ) : null}
          {d.totalTokens != null ? <span>{fmtTokShort(d.totalTokens)}</span> : null}
          {d.turnsUsed != null ? (
            <span>
              {d.turnsUsed}
              {d.budgetTurns ? `/${d.budgetTurns}` : ""} turns
            </span>
          ) : null}
          {d.artifactCount ? (
            <span title={`${d.artifactCount} artifact(s)`}>▦{d.artifactCount}</span>
          ) : null}
        </div>
      ) : null}
      <Handle type="source" position={Position.Bottom} className="fn-handle" />
    </div>
  );
}

// ── Cascade pill (owner, 2026-10-02) ──────────────────────────────────────
/** The pill's status words — every state says what is happening, "pending" included. */
const PILL_STATUS_LABEL: Record<NodeRunStatus, string> = {
  pending: "not started",
  running: "running",
  completed: "done",
  failed: "failed",
  "awaiting-approval": "needs approval",
  waiting: "waiting on a person",
  queued: "queued",
  held: "held",
  skipped: "skipped",
};

/** One text glyph per status (never emoji — they inherit the status colour). */
const PILL_STATUS_ICON: Record<NodeRunStatus, string> = {
  pending: "○",
  running: "◐",
  completed: "✓",
  failed: "✕",
  "awaiting-approval": "⚑",
  waiting: "☗",
  queued: "⋯",
  held: "‖",
  skipped: "⤼",
};

/**
 * The cascade layout's node: a wide pill that reads on its own — status (colour + icon + words),
 * the step's title, who runs it and on what model/effort, the first lines of the prompt it runs on
 * (a person's step: the question), and the measured facts (attempts, duration, tokens). The prompt
 * is clamped to four lines; "full prompt" opens the whole text with a Copy button. Clicking the
 * pill anywhere else still pins the inspector, exactly as the classic node does.
 */
function RunPillView({ data }: NodeProps) {
  const d = data as RunNodeData;
  const canBreak = !!d.onToggleBreakpoint && !!d.nodeId;
  const prompt = d.prompt;
  const hasPrompt = !!prompt && prompt.kind !== "none" && prompt.text.length > 0;
  return (
    <div
      className={`fn-run-pill-node status-${d.status}${d.current ? " current" : ""}${
        d.breakpoint ? " has-breakpoint" : ""
      }${d.heldHere ? " held" : ""}`}
    >
      <Handle type="target" position={Position.Left} className="fn-handle" />
      {canBreak ? (
        <button
          type="button"
          className={`fn-run-bp${d.breakpoint ? " on" : ""}`}
          title={d.breakpoint ? "Remove breakpoint" : "Break before this node"}
          aria-label={d.breakpoint ? `Remove breakpoint on ${d.label}` : `Break before ${d.label}`}
          aria-pressed={!!d.breakpoint}
          onClick={(e) => {
            e.stopPropagation();
            d.onToggleBreakpoint?.(String(d.nodeId));
          }}
          onMouseDown={(e) => e.stopPropagation()}
        />
      ) : null}
      <div className="fn-pill-head">
        <span
          className="fn-pill-status-icon"
          aria-hidden="true"
        >
          {PILL_STATUS_ICON[d.status] ?? "○"}
        </span>
        <span className="fn-pill-title" title={d.label}>
          {d.childIndex != null ? (
            <span className="fn-run-child-badge" title={`batch #${d.childIndex}`}>
              #{d.childIndex}
            </span>
          ) : null}
          {d.label}
        </span>
        <span className="fn-pill-status">
          {d.heldHere ? "⏸ " : ""}
          {PILL_STATUS_LABEL[d.status] ?? d.status}
        </span>
      </div>
      <div className="fn-pill-who">
        <span
          className={`fn-pill-exec${d.executor ? ` exec-${d.executor}` : ""}`}
          title={d.executorTitle ?? (d.executor ? EXECUTOR_TITLE[d.executor] : undefined)}
        >
          {d.executor ? `${EXECUTOR_GLYPH[d.executor] ?? "◈"} ` : ""}
          {d.executorLabel ?? (d.ownerRole || "step")}
        </span>
        {d.executorRole ? <span className="fn-pill-role">{d.executorRole}</span> : null}
        {d.modelLine ? (
          <span className="fn-pill-model" title={d.model ?? d.modelLine}>
            {d.modelLine}
          </span>
        ) : null}
      </div>
      {d.tile ? <TileChip tile={d.tile} onOpenTile={d.onOpenTile} /> : null}
      {d.queuedReason ? (
        <div className="fn-node-queued" title={`queued: ${d.queuedReason}`}>
          queued · {d.queuedReason}
        </div>
      ) : null}
      {hasPrompt ? (
        <div className={`fn-pill-prompt kind-${prompt!.kind}`}>
          {/* The open control heads the box, so a pill squeezed by its max-height clips prompt
              lines, never the way to read them all. */}
          <div className="fn-pill-prompt-head">
            <span>{prompt!.kind === "question" ? "asks" : "prompt"}</span>
            <button
              type="button"
              className="fn-pill-prompt-open"
              title={prompt!.kind === "question" ? "Read the whole question" : "Read the whole prompt"}
              aria-label={`Full ${prompt!.kind === "question" ? "question" : "prompt"} of ${d.label}`}
              onClick={(e) => {
                e.stopPropagation();
                d.onShowPrompt?.(String(d.nodeId));
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              ⤢ full {prompt!.kind === "question" ? "question" : "prompt"}
            </button>
          </div>
          <div className="fn-pill-prompt-text">{prompt!.text}</div>
        </div>
      ) : (
        <div className="fn-pill-prompt empty">no prompt — {d.executorLabel ?? "this step"} runs without one</div>
      )}
      {d.status === "running" && d.lastEvent ? (
        <div className="fn-node-live" title={d.lastEvent}>
          {d.lastEvent}
        </div>
      ) : null}
      {d.attempts ||
      d.durationMs != null ||
      (d.status === "running" && d.startedAt) ||
      d.totalTokens != null ||
      d.turnsUsed != null ||
      d.artifactCount ||
      d.subflowSummary ? (
        <div className="fn-pill-stats">
          {d.attempts ? (
            <span title="attempts, retries included">
              {d.attempts} {d.attempts === 1 ? "attempt" : "attempts"}
            </span>
          ) : null}
          {d.durationMs != null ? (
            <span title="duration">{fmtDurShort(d.durationMs)}</span>
          ) : d.status === "running" && d.startedAt ? (
            <span className="live">{fmtDurShort(Date.now() - d.startedAt)}…</span>
          ) : null}
          {d.totalTokens != null ? <span title="tokens">{fmtTokShort(d.totalTokens)}</span> : null}
          {d.turnsUsed != null ? (
            <span>
              {d.turnsUsed}
              {d.budgetTurns ? `/${d.budgetTurns}` : ""} turns
            </span>
          ) : null}
          {d.artifactCount ? (
            <span title={`${d.artifactCount} artifact(s)`}>▦{d.artifactCount}</span>
          ) : null}
          {d.subflowSummary ? (
            <span
              className={`fn-node-subflow-badge${d.subflowSummary.running > 0 ? " live" : ""}`}
              title="subflow children — click to open the subflow box"
            >
              {d.subflowSummary.running > 0 ? "▶ " : ""}
              {d.subflowSummary.done + d.subflowSummary.failed}/{d.subflowSummary.total} children
            </span>
          ) : null}
        </div>
      ) : null}
      <Handle type="source" position={Position.Right} className="fn-handle" />
    </div>
  );
}

/** Copy to the clipboard; the textarea fallback covers hosts where the async API is refused. */
async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the legacy path
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

function CopyButton({ text, label = "Copy" }: { text: string; label?: string }) {
  const [state, setState] = React.useState<"idle" | "copied" | "failed">("idle");
  React.useEffect(() => {
    if (state === "idle") return;
    const t = window.setTimeout(() => setState("idle"), 1600);
    return () => window.clearTimeout(t);
  }, [state]);
  return (
    <button
      type="button"
      className="fn-btn fn-prompt-copy"
      onClick={(e) => {
        e.stopPropagation();
        void copyText(text).then((ok) => setState(ok ? "copied" : "failed"));
      }}
    >
      {state === "copied" ? "Copied" : state === "failed" ? "Copy failed" : label}
    </button>
  );
}

/**
 * The full words a step runs on, over the canvas: the authored prompt (or a person's question and
 * its choices) and, once the step has run, the prompt exactly as it was sent. Esc or ✕ closes it.
 */
function PromptViewer({
  label,
  executor,
  modelLine,
  prompt,
  onClose,
}: {
  label: string;
  executor?: string;
  modelLine?: string;
  prompt: PillPrompt;
  onClose: () => void;
}) {
  const hasSent = !!prompt.asSent && prompt.asSent !== prompt.text;
  const [tab, setTab] = React.useState<"authored" | "sent">("authored");
  const closeRef = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const text = tab === "sent" && hasSent ? prompt.asSent! : prompt.text;
  const noun = prompt.kind === "question" ? "Question" : "Prompt";
  return (
    <div className="fn-prompt-viewer" role="dialog" aria-label={`${noun} of ${label}`}>
      <div className="fn-prompt-viewer-head">
        <div className="fn-prompt-viewer-title">
          <span>{label}</span>
          <span className="fn-prompt-viewer-sub">
            {[executor, modelLine].filter(Boolean).join(" · ")}
          </span>
        </div>
        <CopyButton text={text} />
        <button
          ref={closeRef}
          type="button"
          className="fn-btn fn-prompt-close"
          aria-label="Close"
          title="Close (Esc)"
          onClick={onClose}
        >
          ✕
        </button>
      </div>
      {hasSent ? (
        <div className="fn-prompt-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "authored"}
            className={`fn-canvas-chip${tab === "authored" ? " on" : ""}`}
            onClick={() => setTab("authored")}
          >
            {noun} as written
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "sent"}
            className={`fn-canvas-chip${tab === "sent" ? " on" : ""}`}
            onClick={() => setTab("sent")}
          >
            As sent to the executor
          </button>
        </div>
      ) : null}
      <pre className="fn-prompt-viewer-text">{text}</pre>
      {prompt.kind === "question" && prompt.options && tab === "authored" ? (
        <div className="fn-prompt-options">
          {prompt.options.map((o) => (
            <span key={o} className="fn-run-pill">
              {o}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** The interest vector's `toward`, for the header chip — the flow's destination in one phrase. */
function interestOfFlow(flow: FlowDefinition): string | undefined {
  const interest = (flow as { interest?: { toward?: unknown } }).interest;
  const toward = interest && typeof interest.toward === "string" ? interest.toward : undefined;
  return toward && toward.length > 60 ? toward.slice(0, 57) + "…" : toward;
}

function interestTitle(flow: FlowDefinition): string {
  const interest = (flow as { interest?: unknown }).interest;
  return interest ? "Interest vector\n" + JSON.stringify(interest, null, 2) : "";
}

/** "claude-sonnet-5" → "sonnet-5"; "local:lclgemma" stays. Bubble-width honesty. */
function shortModelName(model: string): string {
  return model.replace(/^claude-/, "");
}

function fmtDurShort(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const secs = Math.round(ms / 1000);
  if (secs < 90) return `${secs}s`;
  return `${Math.floor(secs / 60)}m${secs % 60 ? ` ${secs % 60}s` : ""}`;
}

function fmtTokShort(tok: number): string {
  return tok >= 1000 ? `${(tok / 1000).toFixed(tok >= 10000 ? 0 : 1)}k tok` : `${tok} tok`;
}

/**
 * The SUBFLOW INSET (visualizer v4): the "box in the flow window" — child runs compartmentalized
 * INSIDE the run canvas, never a new window. Up to two RUNNING children render side-by-side as
 * live mini-chains (their own flow's nodes as status-ringed pills); every child gets a chip.
 * Deliberately not a nested React Flow: a child ladder is a chain, and a pill chain reads faster
 * than a canvas-in-canvas.
 */
function SubflowInset({
  childRuns,
  onClose,
}: {
  childRuns: Record<string, ChildRunState>;
  onClose: () => void;
}) {
  const all = Object.values(childRuns).sort((a, b) => a.startedAt - b.startedAt);
  const running = all.filter((c) => c.status === "running");
  // v4.1: click a child → ZOOM IN. The expanded view takes the whole box: token-spread bar,
  // per-node cards, and the actual result on node click. Back returns to the thread view.
  const [expandedId, setExpandedId] = React.useState<string | null>(null);
  const expanded = expandedId ? childRuns[expandedId] : undefined;
  // "if there is more than one thread — show two in this box": running threads first, newest
  // completed fill the remaining pane when fewer than two run.
  const panes = [...running, ...all.filter((c) => c.status !== "running").reverse()].slice(0, 2);
  return (
    <div
      className={`fn-subflow-inset${expanded ? " expanded" : ""}`}
      role="region"
      aria-label="Subflow child runs"
    >
      <div className="fn-subflow-inset-head">
        {expanded ? (
          <button
            type="button"
            className="fn-subflow-inset-back"
            onClick={() => setExpandedId(null)}
          >
            ← threads
          </button>
        ) : null}
        <span className="fn-subflow-inset-title">
          {expanded
            ? `${expanded.flowId} · ${expanded.childInstanceId.slice(-6)} · ${expanded.status}`
            : `Subflows · ${running.length} running · ${all.length - running.length} finished`}
        </span>
        <button
          type="button"
          className="fn-subflow-inset-close"
          onClick={onClose}
          aria-label="Close subflow box"
        >
          ×
        </button>
      </div>
      {expanded ? (
        <ChildDetail child={expanded} />
      ) : (
        <>
          <div className="fn-subflow-inset-panes">
            {panes.map((c) => (
              <ChildMiniGraph
                key={c.childInstanceId}
                child={c}
                onExpand={() => setExpandedId(c.childInstanceId)}
              />
            ))}
            {panes.length === 0 ? (
              <div className="fn-subflow-inset-empty">No child runs yet.</div>
            ) : null}
          </div>
          {all.length > 0 ? (
            <div className="fn-subflow-inset-chips">
              {all.map((c, i) => (
                <button
                  key={c.childInstanceId}
                  type="button"
                  className={`fn-subflow-chip status-${c.status}`}
                  title={`${c.flowId} · ${c.childInstanceId} — click to zoom in`}
                  onClick={() => setExpandedId(c.childInstanceId)}
                >
                  {c.status === "running" ? "▶" : c.status === "failed" ? "✗" : "✓"} #{i}
                  {" · "}
                  {fmtTokShort(childTotalTokens(c))}
                </button>
              ))}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

/** Σ tokens across a child's nodes — the chip-level spread summary. */
function childTotalTokens(c: ChildRunState): number {
  return Object.values(c.metas ?? {}).reduce((sum, m) => sum + (m.totalTokens ?? 0), 0);
}

/**
 * The ZOOM-IN (v4.1): one child run, full box — a token-spread bar (each node's share of the
 * child's total, proportional widths), per-node cards with the measured work, and the node's
 * actual RESULT text on click. Everything here is measured or verbatim; nothing estimated.
 */
function ChildDetail({ child }: { child: ChildRunState }) {
  const nodes = child.flow?.nodes ?? [];
  const total = childTotalTokens(child) || 1;
  const [selected, setSelected] = React.useState<string | null>(null);
  const selectedResult = selected ? child.results?.[selected] : undefined;
  return (
    <div className="fn-child-detail">
      <div className="fn-child-spread" title="How the tokens are spread across the child's nodes">
        {nodes.map((cn) => {
          const tok = child.metas?.[cn.nodeId]?.totalTokens ?? 0;
          const pct = Math.max(2, Math.round((tok / total) * 100));
          return (
            <div
              key={cn.nodeId}
              className={`fn-child-spread-seg status-${child.statuses[cn.nodeId] || "pending"}`}
              style={{ width: `${pct}%` }}
              title={`${cn.nodeId}: ${tok.toLocaleString()} tokens`}
            >
              {cn.nodeId}
            </div>
          );
        })}
      </div>
      <div className="fn-child-detail-cards">
        {nodes.map((cn) => {
          const st = child.statuses[cn.nodeId] || "pending";
          const m = child.metas?.[cn.nodeId];
          const cur = child.currentNodeId === cn.nodeId;
          return (
            <button
              key={cn.nodeId}
              type="button"
              className={`fn-child-card status-${st}${cur ? " current" : ""}${selected === cn.nodeId ? " selected" : ""}`}
              onClick={() => setSelected(selected === cn.nodeId ? null : cn.nodeId)}
              title={`${cn.name || cn.nodeId} — click for the result`}
            >
              <span className="fn-child-card-name">{cn.nodeId}</span>
              <span className="fn-child-card-stats">
                {m?.model ? <span>{shortModelName(m.model)}</span> : null}
                {m?.totalTokens != null ? <span>{fmtTokShort(m.totalTokens)}</span> : null}
                {m?.durationMs != null ? (
                  <span>{fmtDurShort(m.durationMs)}</span>
                ) : st === "running" && m?.startedAt ? (
                  <span className="live">{fmtDurShort(Date.now() - m.startedAt)}…</span>
                ) : null}
                {m?.turnsUsed != null ? <span>{m.turnsUsed}t</span> : null}
              </span>
            </button>
          );
        })}
      </div>
      {selectedResult !== undefined ? (
        <pre className="fn-child-result">{selectedResult}</pre>
      ) : selected ? (
        <div className="fn-subflow-inset-empty">No result recorded for {selected} yet.</div>
      ) : (
        <div className="fn-subflow-inset-empty">Click a node for its result.</div>
      )}
    </div>
  );
}

/** One child run as a live pill chain: the child flow's nodes in order, status-ringed. */
function ChildMiniGraph({ child, onExpand }: { child: ChildRunState; onExpand?: () => void }) {
  const nodes = child.flow?.nodes ?? [];
  const shortId = child.childInstanceId.slice(-6);
  const totalTok = childTotalTokens(child);
  return (
    <div className={`fn-child-mini status-${child.status}`}>
      <button
        type="button"
        className="fn-child-mini-head"
        title={`${child.childInstanceId} — click to zoom in`}
        onClick={onExpand}
      >
        <span className={`fn-child-mini-dot status-${child.status}`} />
        {child.flowId} · {shortId}
        {totalTok > 0 ? <span className="fn-child-mini-tok">{fmtTokShort(totalTok)}</span> : null}
        <span className="fn-child-mini-zoom" aria-hidden="true">
          ⤢
        </span>
      </button>
      <div className="fn-child-mini-chain">
        {nodes.length > 0 ? (
          nodes.map((cn, i) => {
            const st = child.statuses[cn.nodeId] || "pending";
            const cur = child.currentNodeId === cn.nodeId;
            return (
              <React.Fragment key={cn.nodeId}>
                {i > 0 ? <span className="fn-child-mini-arrow">→</span> : null}
                <span
                  className={`fn-child-mini-node status-${st}${cur ? " current" : ""}`}
                  title={
                    `${cn.name || cn.nodeId}: ${st}` +
                    (child.metas?.[cn.nodeId]?.totalTokens != null
                      ? ` · ${child.metas[cn.nodeId].totalTokens!.toLocaleString()} tokens`
                      : "")
                  }
                >
                  {cn.nodeId}
                  {child.metas?.[cn.nodeId]?.totalTokens != null ? (
                    <span className="fn-child-mini-node-tok">
                      {fmtTokShort(child.metas[cn.nodeId].totalTokens!)}
                    </span>
                  ) : null}
                </span>
              </React.Fragment>
            );
          })
        ) : (
          <span className="fn-child-mini-loading">loading child flow…</span>
        )}
      </div>
    </div>
  );
}

// Artifact node (flow-visualizer-v3 R2/W2b): a square glyph rather than the flowNode card shape,
// so an artifact reads as a FILE on the canvas, not a step — the same distinction sourceNode draws
// for declared inputs (styles.css). `unreadBy` (Q3) is a correctness warning, not decoration: a
// handle nobody read is exactly the shape of bug the data-plane rewrite exists to make visible.
function ArtifactNodeView({ data }: NodeProps) {
  const d = data as ArtifactNodeData;
  const unread = (d.unreadBy?.length ?? 0) > 0;
  const stale = (d.staleReaders?.length ?? 0) > 0;
  const rowsLabel = d.rows != null ? `${d.rows.toLocaleString()} rows` : undefined;
  return (
    <div
      className={`fn-artifact-node${unread ? " unread" : ""}${stale ? " stale" : ""}`}
      title={d.path}
    >
      <Handle type="target" position={Position.Top} className="fn-handle" />
      <div className="fn-artifact-glyph" aria-hidden="true">
        ▦
      </div>
      <div className="fn-artifact-name">{baseName(d.path)}</div>
      <div className="fn-artifact-badge">
        {fmtBytes(d.bytes)}
        {rowsLabel ? ` · ${rowsLabel}` : ""}
        {/* flow-shared-context R3: a version above v1 means the file was REwritten during the run. */}
        {d.version != null && d.version > 1 ? (
          <span
            className="fn-artifact-version"
            title={`Rewritten ${d.version - 1} time(s) this run`}
          >
            {` · v${d.version}`}
          </span>
        ) : null}
      </div>
      {unread ? (
        <div className="fn-artifact-unread-flag" title={`Unread by: ${d.unreadBy.join(", ")}`}>
          ⚠ unread
        </div>
      ) : null}
      {stale ? (
        <div
          className="fn-artifact-stale-flag"
          title={`Read an older version: ${d.staleReaders.join(", ")}`}
        >
          ⟳ stale readers
        </div>
      ) : null}
      <Handle type="source" position={Position.Bottom} className="fn-handle" />
    </div>
  );
}

const nodeTypes = { flowNode: RunNodeView, artifactNode: ArtifactNodeView, runPill: RunPillView };
const edgeTypes = { floating: FloatingEdge, cascade: CascadeEdge };

/** Human byte size — matches the granularity the inspector's "Bytes" fact uses. */
function fmtBytes(n: number): string {
  if (!Number.isFinite(n)) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDuration(ms?: number): string {
  if (ms == null) return "";
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  if (s < 90) return `${s.toFixed(1)}s`;
  return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
}

/** Wall-clock HH:MM:SS for a drill-down row; "—" when unknown. */
function fmtClock(at?: number): string {
  if (!at) return "—";
  const d = new Date(at);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

/** Last path segment (posix or windows separators). */
function baseName(p: string): string {
  const parts = p.split(/[\\/]/);
  return parts[parts.length - 1] || p;
}

// ── Live bubble (WU-3, FR-3.1-3.3) ────────────────────────────────────────
/** Bubble thinking-snippet budget (FR-3.1: ~160 chars). */
const RUN_BUBBLE_SNIPPET_CHARS = 160;
/** Flow-space px the bubble sits above the running node's top edge. */
const RUN_BUBBLE_OFFSET_Y = 92;
/** Files-in-progress shown in the bubble (FR-3.1: up to 3). */
const RUN_BUBBLE_MAX_FILES = 3;

function truncateSnippet(s: string, max: number): string {
  return s.length > max ? s.slice(0, max) + "…" : s;
}

/**
 * Last thinking/content chunk from the node's live transcript, flattened to
 * one line and trimmed to {@link RUN_BUBBLE_SNIPPET_CHARS} (FR-3.1).
 */
function lastThinkingSnippet(trace: NodeTraceEntry[] | undefined): string | undefined {
  if (!trace) {
    return undefined;
  }
  for (let i = trace.length - 1; i >= 0; i--) {
    const e = trace[i];
    if (e.kind === "thinking" || e.kind === "content") {
      const flat = e.text.replace(/\s+/g, " ").trim();
      return flat ? truncateSnippet(flat, RUN_BUBBLE_SNIPPET_CHARS) : undefined;
    }
  }
  return undefined;
}

/**
 * Floating "what's happening now" bubble for the running node (FR-3): last
 * thinking snippet, current tool line (latest ActionRecord summary), and up
 * to 3 files-in-progress. Rendered via {@link ViewportPortal} so its parent
 * positions it in flow (canvas) space — this component only lays out its own
 * content; `pointer-events: none` (styles.css) lets canvas gestures pass
 * through it untouched.
 */
function RunBubble({
  trace,
  actions,
  x,
  y,
}: {
  trace: NodeTraceEntry[] | undefined;
  actions: ActionRecord[] | undefined;
  x: number;
  y: number;
}) {
  const records = actions ?? [];
  const thinking = lastThinkingSnippet(trace);
  const toolLine = records.length > 0 ? records[records.length - 1].summary : undefined;
  const files = deriveFiles(records, { runningOnly: true }).slice(0, RUN_BUBBLE_MAX_FILES);

  if (!thinking && !toolLine && files.length === 0) {
    return null;
  }

  return (
    <div
      className="fn-run-bubble"
      style={{ transform: `translate(${x}px, ${y}px)` }}
      role="status"
      aria-live="polite"
    >
      {thinking ? <div className="fn-run-bubble-thinking">{thinking}</div> : null}
      {toolLine ? (
        <div className="fn-run-bubble-tool" title={toolLine}>
          {toolLine}
        </div>
      ) : null}
      {files.length > 0 ? (
        <div className="fn-run-bubble-files">
          {files.map((f) => (
            <span key={f} className="fn-run-bubble-file" title={f}>
              {baseName(f)}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The interactive read-only run graph: React Flow canvas + status coloring +
 * the live bubble overlay. Extracted so it can be shared, byte-for-byte,
 * between the wide layout's canvas pane and compact mode's expanded graph
 * view (WU-5, FR-5.1) — both must render identically.
 */
function RunCanvas({
  nodes,
  edges,
  onNodeClick,
  onPaneClick,
  onInit,
  showBubble,
  bubbleNode,
  traces,
  actions,
}: {
  nodes: Node[];
  edges: Edge[];
  onNodeClick: (evt: React.MouseEvent, node: Node) => void;
  onPaneClick: () => void;
  onInit: (instance: ReactFlowInstance) => void;
  showBubble: boolean;
  bubbleNode: Node | undefined;
  traces: Record<string, NodeTraceEntry[]>;
  actions: Record<string, ActionRecord[]>;
}) {
  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      nodesDraggable={false}
      nodesConnectable={false}
      elementsSelectable={false}
      onNodeClick={onNodeClick}
      onPaneClick={onPaneClick}
      onInit={onInit}
      fitView
      // A cascade of 340px pills is wide; React Flow's default 0.5 floor would refuse to fit it.
      minZoom={0.1}
      proOptions={{ hideAttribution: true }}
    >
      <Background gap={16} />
      <Controls showInteractive={false} />
      <MiniMap pannable zoomable />
      {showBubble && bubbleNode ? (
        <ViewportPortal>
          <RunBubble
            trace={traces[bubbleNode.id]}
            actions={actions[bubbleNode.id]}
            x={bubbleNode.position.x}
            y={bubbleNode.position.y - RUN_BUBBLE_OFFSET_Y}
          />
        </ViewportPortal>
      ) : null}
    </ReactFlow>
  );
}

// ── Compact / mobile layout (WU-5, FR-5) ──────────────────────────────────
/**
 * Container width below which {@link FlowRunView} switches to the compact
 * layout (FR-5.1). Measured via `ResizeObserver` on the component's own
 * shell — deliberately NOT a media query, so a narrow host panel (a VS Code
 * side panel, an embedded iframe column) triggers it even inside a wide
 * browser viewport.
 */
const COMPACT_BREAKPOINT_PX = 700;

/**
 * Inspector/paused-card sidebar width bounds (flow-visualizer-v3 R3). `DEFAULT` matches the
 * pre-resize hardcoded `.fn-panel` width so an unresized run view is pixel-identical to before.
 * `MIN` keeps the facts column from wrapping into illegibility; `MAX` keeps the canvas from being
 * squeezed to nothing on a wide-but-not-huge window.
 */
export const RUN_PANEL_MIN_WIDTH = 260;
export const RUN_PANEL_MAX_WIDTH = 640;
export const RUN_PANEL_DEFAULT_WIDTH = 360;

/** Clamps to the bounds above; a non-finite input (corrupt persisted value, absent prop) falls back to the default rather than propagating NaN into a CSS var. */
function clampPanelWidth(width: number): number {
  if (!Number.isFinite(width)) {
    return RUN_PANEL_DEFAULT_WIDTH;
  }
  return Math.min(RUN_PANEL_MAX_WIDTH, Math.max(RUN_PANEL_MIN_WIDTH, width));
}

/**
 * A node's elapsed time for the compact header card (FR-5.1): the finished
 * duration once known, else a live `now - startedAt` while still running.
 */
function nodeElapsedMs(meta: NodeRunMeta | undefined, nowMs: number): number | undefined {
  if (!meta || meta.startedAt == null) {
    return undefined;
  }
  return meta.durationMs != null ? meta.durationMs : Math.max(0, nowMs - meta.startedAt);
}

/**
 * The most recently COMPLETED node other than `excludeId` — compact mode's
 * "previous node" strip (FR-5.1): picked by latest known end time so a long
 * or looping flow always surfaces the neighbour that just finished. The
 * summary prefers the node's latest `result` ActionRecord (FR-1), falling
 * back to its decision or last activity line, then a plain "Completed."
 */
function previousCompletedNode(
  nodes: Node[],
  statuses: Record<string, NodeRunStatus>,
  metas: Record<string, NodeRunMeta>,
  actions: Record<string, ActionRecord[]>,
  excludeId: string | null,
): { id: string; label: string; summary: string } | null {
  let bestId: string | null = null;
  let bestEnd = -1;
  for (const n of nodes) {
    if (n.id === excludeId) {
      continue;
    }
    if ((statuses[n.id] || "pending") !== "completed") {
      continue;
    }
    const meta = metas[n.id];
    const end = meta?.startedAt != null ? meta.startedAt + (meta.durationMs ?? 0) : 0;
    if (end >= bestEnd) {
      bestEnd = end;
      bestId = n.id;
    }
  }
  if (!bestId) {
    return null;
  }
  const node = nodes.find((n) => n.id === bestId);
  const label = node ? String((node.data as RunNodeData).label ?? bestId) : bestId;
  const records = actions[bestId] ?? [];
  let summary: string | undefined;
  for (let i = records.length - 1; i >= 0; i--) {
    if (records[i].kind === "result") {
      summary = records[i].summary;
      break;
    }
  }
  if (!summary) {
    const meta = metas[bestId];
    summary = meta?.decision ? `→ ${meta.decision}` : meta?.lastEvent || "Completed.";
  }
  return { id: bestId, label, summary };
}

/** Compact header card: the inspected node's identity + live vitals (FR-5.1). */
function RunCompactCard({
  label,
  status,
  model,
  elapsedMs,
  isLive,
}: {
  label: string;
  status: NodeRunStatus;
  model: string | undefined;
  elapsedMs: number | undefined;
  isLive: boolean;
}) {
  const statusText = STATUS_LABEL[status] || status;
  return (
    <div className="fn-run-compact-card" role="group" aria-label="Current node">
      <div className="fn-run-compact-card-name" title={label}>
        {label}
        {isLive ? <span className="fn-run-live-dot" title="currently executing" /> : null}
      </div>
      <div className="fn-run-compact-card-meta">
        {statusText ? (
          <span className={`fn-run-compact-card-status fn-run-header-status-${status}`}>
            {statusText}
          </span>
        ) : null}
        {model ? (
          <span className="fn-run-compact-card-model" title={model}>
            {model}
          </span>
        ) : null}
        {elapsedMs != null ? (
          <span className="fn-run-compact-card-elapsed">{fmtDuration(elapsedMs)}</span>
        ) : null}
      </div>
    </div>
  );
}

/** One-line previous-node result summary shown above the current-node card. */
function RunCompactPrev({ label, summary }: { label: string; summary: string }) {
  return (
    <div
      className="fn-run-compact-prev"
      role="note"
      aria-label={`Previous node ${label}: ${summary}`}
      title={`${label}: ${summary}`}
    >
      <span className="fn-run-compact-prev-tag">prev</span>
      <span className="fn-run-compact-prev-label">{label}</span>
      <span className="fn-run-compact-prev-summary">{summary}</span>
    </div>
  );
}

/**
 * Small, non-interactive graph preview pinned top-right in compact mode
 * (FR-5.1): a real (tiny) React Flow render so it always matches the actual
 * layout, but with all interaction disabled — the whole card is one tap
 * target that expands to the full graph. `pointer-events: none` on the
 * embedded canvas (styles.css) lets the tap land on this wrapper no matter
 * where inside the thumbnail it occurs (same technique as {@link RunBubble}).
 */
function RunCompactThumb({
  nodes,
  edges,
  onExpand,
  btnRef,
}: {
  nodes: Node[];
  edges: Edge[];
  onExpand: () => void;
  /** Refocused when Esc collapses the expanded graph back to this thumb (a11y). */
  btnRef?: React.Ref<HTMLDivElement>;
}) {
  return (
    <div
      ref={btnRef}
      className="fn-run-compact-thumb"
      role="button"
      tabIndex={0}
      aria-label="Expand flow graph"
      title="Expand flow graph"
      onClick={onExpand}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onExpand();
        }
      }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        panOnDrag={false}
        zoomOnScroll={false}
        zoomOnPinch={false}
        zoomOnDoubleClick={false}
        preventScrolling={false}
        proOptions={{ hideAttribution: true }}
        minZoom={0.05}
        fitView
      >
        <Background gap={20} />
      </ReactFlow>
    </div>
  );
}

/**
 * The compact / mobile layout (WU-5, FR-5.1-5.3): a graph thumbnail (tap to
 * expand to the full interactive graph and back), the inspected node's
 * header card, the previous node's one-line result summary, and that node's
 * live transcript as the main scroll area. Reuses {@link RunCanvas} for the
 * expanded graph and {@link RunTranscript} for the transcript so both stay
 * pixel-identical to their wide-layout counterparts. The Log toggle (header,
 * FR-5.2) and drill-downs stay reachable exactly as in the wide layout —
 * this component only replaces the canvas + inspector pane.
 */
function RunCompact({
  nodes,
  edges,
  graphExpanded,
  onToggleGraph,
  onNodeClick,
  onPaneClick,
  onCanvasInit,
  prev,
  label,
  status,
  model,
  elapsedMs,
  isLive,
  trace,
  thumbBtnRef,
}: {
  nodes: Node[];
  edges: Edge[];
  graphExpanded: boolean;
  onToggleGraph: () => void;
  onNodeClick: (evt: React.MouseEvent, node: Node) => void;
  onPaneClick: () => void;
  onCanvasInit: (instance: ReactFlowInstance) => void;
  prev: { id: string; label: string; summary: string } | null;
  label: string;
  status: NodeRunStatus;
  model: string | undefined;
  elapsedMs: number | undefined;
  isLive: boolean;
  trace: NodeTraceEntry[] | undefined;
  /** Refocused when Esc collapses the expanded graph (a11y). */
  thumbBtnRef?: React.Ref<HTMLDivElement>;
}) {
  if (graphExpanded) {
    return (
      <div className="fn-canvas fn-run-compact-graph">
        <button
          type="button"
          className="fn-run-compact-graph-close"
          onClick={onToggleGraph}
          aria-label="Collapse graph, back to node view"
          title="Back to node view (Esc)"
        >
          <span aria-hidden="true">✕</span> Graph
        </button>
        <RunCanvas
          nodes={nodes}
          edges={edges}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          onInit={onCanvasInit}
          showBubble={false}
          bubbleNode={undefined}
          traces={{}}
          actions={{}}
        />
      </div>
    );
  }
  return (
    <div className="fn-run-compact" role="region" aria-label="Current node run">
      <div className="fn-run-compact-top">
        <div className="fn-run-compact-top-info">
          {prev ? <RunCompactPrev label={prev.label} summary={prev.summary} /> : null}
          <RunCompactCard
            label={label}
            status={status}
            model={model}
            elapsedMs={elapsedMs}
            isLive={isLive}
          />
        </div>
        <RunCompactThumb
          nodes={nodes}
          edges={edges}
          onExpand={onToggleGraph}
          btnRef={thumbBtnRef}
        />
      </div>
      <div className="fn-run-compact-transcript-wrap">
        {trace && trace.length > 0 ? (
          <RunTranscript entries={trace} live={isLive} />
        ) : (
          <div className="fn-run-compact-empty">No activity yet.</div>
        )}
      </div>
    </div>
  );
}

/** Single-glyph icon per action kind (theme-safe, no emoji dependency). */
const KIND_ICON: Record<ActionKind, string> = {
  tool: "⚙",
  edit: "✎",
  read: "▤",
  command: "❯",
  "thinking-marker": "…",
  transition: "→",
  error: "!",
  approval: "✓",
  result: "◆",
};

/**
 * What a drill-down is scoped to (WU-2 / FR-2.1-2.2): a tool pill filters to
 * that tool's records; a created pill shows the node's output record(s) the
 * directive was parsed from. `label` heads the drill-down section.
 */
type DrillFilter =
  | { type: "tool"; tool: string; label: string }
  | { type: "created"; label: string };

/**
 * The records a drill-down shows for a filter (FR-2.1/2.2). Tool → that tool's
 * records; created → the node's `result` records (the output directives are
 * parsed from), with a synthesized fallback row from the label when no result
 * record is present (e.g. a replayed run without captured output).
 */
function drillRecords(
  records: ActionRecord[],
  filter: DrillFilter,
  nodeId: string,
): ActionRecord[] {
  if (filter.type === "tool") {
    return filterActions(records, { tool: filter.tool });
  }
  const results = records.filter((r) => r.kind === "result");
  if (results.length > 0) {
    return results;
  }
  return [{ seq: -1, at: 0, nodeId, kind: "result", summary: filter.label }];
}

/** One expandable drill-down row: time, kind icon, summary, capped detail. */
function DrillRow({ record }: { record: ActionRecord }) {
  const [open, setOpen] = React.useState(false);
  const hasDetail = !!record.detail;
  return (
    <div className={`fn-run-drill-row fn-run-drill-kind-${record.kind}`}>
      <button
        type="button"
        className="fn-run-drill-row-head"
        onClick={() => hasDetail && setOpen((o) => !o)}
        aria-expanded={hasDetail ? open : undefined}
        disabled={!hasDetail}
      >
        <span className="fn-run-drill-time">{fmtClock(record.at)}</span>
        <span className="fn-run-drill-icon" aria-hidden="true">
          {KIND_ICON[record.kind]}
        </span>
        <span className="fn-run-drill-summary">{record.summary}</span>
        {hasDetail ? (
          <span className="fn-run-drill-caret" aria-hidden="true">
            {open ? "▾" : "▸"}
          </span>
        ) : null}
      </button>
      {open && record.detail ? <pre className="fn-run-drill-detail">{record.detail}</pre> : null}
    </div>
  );
}

/**
 * In-inspector drill-down (FR-2.1-2.3): replaces the transcript while open,
 * listing a node's pre-filtered ActionRecords. Esc (handled by the inspector)
 * or the ✕ button closes it. Everything is keyboard focusable.
 */
function RunDrillDown({
  records,
  filter,
  capped,
  onClose,
  closeBtnRef,
}: {
  records: ActionRecord[];
  filter: DrillFilter;
  capped: boolean;
  onClose: () => void;
  closeBtnRef: React.Ref<HTMLButtonElement>;
}) {
  const title = filter.type === "tool" ? `${filter.tool} actions` : filter.label;
  return (
    <div className="fn-run-drill" role="region" aria-label={`Drill-down: ${title}`}>
      <div className="fn-run-drill-head">
        <span className="fn-run-drill-title" title={title}>
          {title}
        </span>
        <button
          type="button"
          ref={closeBtnRef}
          className="fn-run-drill-close"
          onClick={onClose}
          aria-label="Close drill-down (Esc)"
          title="Close (Esc)"
        >
          ✕
        </button>
      </div>
      {capped ? (
        <div className="fn-run-drill-cap">
          showing last {MAX_ACTIONS_PER_NODE} actions (older dropped)
        </div>
      ) : null}
      <div className="fn-run-drill-list">
        {records.length === 0 ? (
          <div className="fn-run-drill-empty">No actions recorded.</div>
        ) : (
          records.map((r) => <DrillRow key={r.seq} record={r} />)
        )}
      </div>
    </div>
  );
}

/**
 * Inspector panel for one node's run details. Shows whatever the host has
 * learned so far — persona, model, source, timing, tokens, and pills for each
 * tool (with invocation counts) and skill used. For the running node it also
 * shows the latest activity line, making it the "current task" view.
 */
const TRACE_KIND_LABEL: Record<NodeTraceEntry["kind"], string> = {
  prompt: "prompt",
  thinking: "thinking",
  content: "output",
  tool: "tool",
  result: "result",
  error: "error",
  transition: "decision",
};

/**
 * The node's live transcript — what the chat window streams for this node,
 * scoped to the inspector. Sticks to the bottom while the reader hasn't
 * scrolled up; scrolling up pauses the auto-follow until they return to the
 * bottom (same contract as a chat pane).
 */
/**
 * A trace/action row's text, rendered structurally when it is JSON (flow-visualizer-v3 R4).
 * The owner's words for the old behaviour: a result was "a JSON wall scrolled mid-array — not
 * understandable". JSON payloads collapse to a summary line and pretty-print on demand; short or
 * non-JSON text renders as before. Parse is attempted only on {…}/[…]-shaped text.
 */
function StructuredTraceText({ text }: { text: string }) {
  const trimmed = text.trim();
  const jsonish =
    (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
    (trimmed.startsWith("[") && trimmed.endsWith("]"));
  const parsed = React.useMemo(() => {
    if (!jsonish) return null;
    try {
      const v = JSON.parse(trimmed) as unknown;
      return v && typeof v === "object" ? v : null;
    } catch {
      return null;
    }
  }, [trimmed, jsonish]);
  if (!parsed) {
    return <span className="fn-run-trace-text">{text}</span>;
  }
  const label = Array.isArray(parsed)
    ? `JSON array · ${parsed.length} items`
    : `JSON object · ${Object.keys(parsed as object).length} keys`;
  return (
    <details className="fn-run-trace-json">
      <summary>{label}</summary>
      <pre>{JSON.stringify(parsed, null, 2)}</pre>
    </details>
  );
}

/** One chronological row of the thinking track: a trace entry or a typed action chip. */
type ThinkingRow = { at: number; trace: NodeTraceEntry } | { at: number; action: ActionRecord };

function RunTranscript({
  entries,
  actions,
  live,
}: {
  entries: NodeTraceEntry[];
  /** Typed ActionRecords for the same node — interleaved by time as tool chips (v3 R4). */
  actions?: ActionRecord[];
  live: boolean;
}) {
  const boxRef = React.useRef<HTMLDivElement | null>(null);
  const stickRef = React.useRef(true);
  // The thinking track: trace entries and action chips merged by wall-clock. Stable sort keeps
  // same-timestamp neighbours in stream order (trace first — the thought precedes its tool call).
  const rows = React.useMemo<ThinkingRow[]>(() => {
    const merged: ThinkingRow[] = [
      ...entries.map((trace) => ({ at: trace.at, trace })),
      ...(actions ?? []).map((action) => ({ at: action.at, action })),
    ];
    return merged.sort((a, b) => a.at - b.at);
  }, [entries, actions]);
  React.useEffect(() => {
    const box = boxRef.current;
    if (box && stickRef.current) {
      box.scrollTop = box.scrollHeight;
    }
  }, [rows.length, entries[entries.length - 1]?.text.length]);
  return (
    <div className="fn-run-pill-group fn-run-transcript-group">
      <div className="fn-run-pill-label">
        Thinking
        {live ? <span className="fn-run-live-dot" title="streaming" /> : null}
      </div>
      <div
        className="fn-run-transcript"
        ref={boxRef}
        onScroll={() => {
          const box = boxRef.current;
          if (!box) return;
          stickRef.current = box.scrollHeight - box.scrollTop - box.clientHeight < 24;
        }}
      >
        {rows.map((row, i) =>
          "trace" in row ? (
            <div key={i} className={`fn-run-trace fn-run-trace-${row.trace.kind}`}>
              <span className="fn-run-trace-kind">{TRACE_KIND_LABEL[row.trace.kind]}</span>
              <StructuredTraceText text={row.trace.text} />
            </div>
          ) : (
            <div
              key={i}
              className={`fn-run-trace fn-run-action-chip fn-run-action-${row.action.kind}`}
            >
              <span className="fn-run-trace-kind">{row.action.tool ?? row.action.kind}</span>
              <span className="fn-run-trace-text">
                {row.action.summary}
                {row.action.file ? (
                  <span className="fn-run-action-file"> {row.action.file}</span>
                ) : null}
              </span>
            </div>
          ),
        )}
      </div>
    </div>
  );
}

function RunInspector({
  nodeId,
  label,
  ownerRole,
  status,
  meta,
  trace,
  actions,
  isCurrent,
  following,
  node,
  pillKinds,
  onOpenArtifact,
  onResizeStart,
  prompt,
  onOpenPrompt,
}: {
  nodeId: string;
  label: string;
  ownerRole: string;
  /** The words the step runs on (cascade pill helpers) — a collapsible section with Copy. */
  prompt?: PillPrompt;
  /** Opens the wide full-prompt viewer over the canvas. */
  onOpenPrompt?: () => void;
  status: NodeRunStatus;
  meta: NodeRunMeta | undefined;
  /** flow-platform F15 — threaded from FlowRunViewProps; absent ⇒ pills stay non-clickable. */
  onOpenArtifact?: (path: string) => void;
  trace: NodeTraceEntry[] | undefined;
  actions: ActionRecord[] | undefined;
  isCurrent: boolean;
  following: boolean;
  /** The node's definition — supplies the DECLARED budgets the pills read against. */
  node?: NodeDefinition;
  /** Which pills the host wants (W3 config). Omitted ⇒ all of them. */
  pillKinds?: ReadonlyArray<string>;
  /** Drag-to-resize the sidebar (flow-visualizer-v3 R3). Absent ⇒ no handle rendered. */
  onResizeStart?: (e: React.PointerEvent<HTMLDivElement>) => void;
}) {
  const tools = Object.entries(meta?.tools ?? {});
  const skills = meta?.skills ?? [];
  const tokens =
    meta?.totalTokens ??
    (meta?.inputTokens != null || meta?.outputTokens != null
      ? (meta?.inputTokens ?? 0) + (meta?.outputTokens ?? 0)
      : undefined);

  // Drill-down state (FR-2.1-2.3). null → normal inspector (transcript shows);
  // a filter → the drill-down replaces the transcript until closed.
  const records = actions ?? [];
  const [drill, setDrill] = React.useState<DrillFilter | null>(null);
  const triggerRef = React.useRef<HTMLElement | null>(null);
  const closeBtnRef = React.useRef<HTMLButtonElement>(null);

  // Reset the drill-down whenever the inspected node changes.
  React.useEffect(() => {
    setDrill(null);
  }, [nodeId]);

  const openDrill = (f: DrillFilter, e: React.MouseEvent<HTMLButtonElement>) => {
    triggerRef.current = e.currentTarget;
    setDrill(f);
  };
  const closeDrill = React.useCallback(() => {
    setDrill(null);
    const t = triggerRef.current;
    if (t) {
      window.requestAnimationFrame(() => t.focus());
    }
  }, []);

  // Esc closes the drill-down; focus the ✕ when it opens (a11y, FR-2.3).
  React.useEffect(() => {
    if (!drill) {
      return;
    }
    if (closeBtnRef.current) {
      closeBtnRef.current.focus();
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        closeDrill();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drill, closeDrill]);

  // Files fact (FR-2.4): in-progress list while running, touched count when done.
  const isRunning = status === "running";
  const filesInProgress = isRunning ? deriveFiles(records, { runningOnly: true }) : [];
  const filesTouched = !isRunning ? deriveFiles(records) : [];

  // Tools that actually have captured records are drill-able; absent the
  // `actions` prop this stays empty so pills render unchanged (NF-1).
  const toolsWithRecords = new Set(records.map((r) => r.tool).filter((t): t is string => !!t));
  const canDrillCreated = records.length > 0;

  return (
    <div className="fn-panel fn-run-inspector">
      {onResizeStart ? (
        <div
          className="fn-run-panel-resize-handle"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize inspector panel"
          onPointerDown={onResizeStart}
        />
      ) : null}
      <div className="fn-panel-title">
        {label}
        {isCurrent ? <span className="fn-run-live-dot" title="currently executing" /> : null}
      </div>
      <div className="fn-run-inspector-sub">
        {nodeId} · {status}
        {following ? " · following run" : ""}
      </div>
      <div className="fn-run-facts">
        {meta?.persona ? <Fact k="Persona" v={meta.persona} /> : null}
        <Fact k="Thinker" v={meta?.thinkerId || ownerRole || "—"} />
        {meta?.model ? <Fact k="Model" v={meta.model} /> : null}
        {meta?.source ? <Fact k="Source" v={meta.source} /> : null}
        {meta?.durationMs != null ? <Fact k="Duration" v={fmtDuration(meta.durationMs)} /> : null}
        {tokens != null ? (
          <Fact
            k="Tokens"
            v={`${tokens.toLocaleString()}${
              meta?.inputTokens != null && meta?.outputTokens != null
                ? ` (${meta.inputTokens.toLocaleString()} in / ${meta.outputTokens.toLocaleString()} out)`
                : ""
            }`}
          />
        ) : null}
        {isRunning && filesInProgress.length > 0 ? (
          <div className="fn-run-fact">
            <span className="fn-run-fact-k">Files</span>
            <span className="fn-run-fact-v fn-run-files">
              {filesInProgress.map((f) => (
                <span key={f} className="fn-run-file" title={f}>
                  {baseName(f)}
                </span>
              ))}
            </span>
          </div>
        ) : !isRunning && filesTouched.length > 0 ? (
          <Fact k="Files" v={`${filesTouched.length} touched`} />
        ) : null}
        {meta?.decision ? <Fact k="Decision" v={`→ ${meta.decision}`} /> : null}
        {meta?.reasoningSessionId ? <Fact k="Reasoning session" v={meta.reasoningSessionId} /> : null}
        {meta?.candidateRecordId ? <Fact k="Candidate record" v={meta.candidateRecordId} /> : null}
        {meta?.evaluationRecordId ? <Fact k="Evaluation record" v={meta.evaluationRecordId} /> : null}
        {meta?.acceptanceStatus ? <Fact k="Acceptance at completion" v={meta.acceptanceStatus} /> : null}
      </div>
      {isCurrent && meta?.lastEvent ? (
        <div className="fn-run-activity" title={meta.lastEvent}>
          {meta.lastEvent}
        </div>
      ) : null}
      {prompt && prompt.kind !== "none" && prompt.text ? (
        <details className="fn-run-prompt-details">
          <summary>
            {prompt.kind === "question" ? "Question" : "Prompt"}
            <span className="fn-run-prompt-actions">
              <CopyButton text={prompt.text} />
              {onOpenPrompt ? (
                <button
                  type="button"
                  className="fn-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    onOpenPrompt();
                  }}
                >
                  ⤢ Open
                </button>
              ) : null}
            </span>
          </summary>
          <pre className="fn-run-prompt-text">{prompt.text}</pre>
        </details>
      ) : null}
      <BudgetPills
        pills={budgetPills(meta, node, { show: pillKinds })}
        onOpenArtifact={onOpenArtifact}
      />
      {tools.length > 0 ? (
        <div className="fn-run-pill-group">
          <div className="fn-run-pill-label">Tools</div>
          <div className="fn-run-pills">
            {tools
              .sort((a, b) => b[1] - a[1])
              .map(([name, count]) => {
                const inner = (
                  <>
                    {name}
                    {count > 1 ? <span className="fn-run-pill-count">×{count}</span> : null}
                  </>
                );
                return toolsWithRecords.has(name) ? (
                  <button
                    key={name}
                    type="button"
                    className="fn-run-pill fn-run-pill-btn"
                    onClick={(e) => openDrill({ type: "tool", tool: name, label: name }, e)}
                    aria-label={`Show ${name} actions`}
                  >
                    {inner}
                  </button>
                ) : (
                  <span key={name} className="fn-run-pill">
                    {inner}
                  </span>
                );
              })}
          </div>
        </div>
      ) : null}
      {skills.length > 0 ? (
        <div className="fn-run-pill-group">
          <div className="fn-run-pill-label">Skills</div>
          <div className="fn-run-pills">
            {skills.map((s) => (
              <span key={s} className="fn-run-pill fn-run-pill-skill">
                {s}
              </span>
            ))}
          </div>
        </div>
      ) : null}
      {(meta?.created?.length ?? 0) > 0 ? (
        <div className="fn-run-pill-group">
          <div className="fn-run-pill-label">Created</div>
          <div className="fn-run-pills">
            {meta!.created!.map((c) =>
              canDrillCreated ? (
                <button
                  key={c}
                  type="button"
                  className="fn-run-pill fn-run-pill-created fn-run-pill-btn"
                  onClick={(e) => openDrill({ type: "created", label: c }, e)}
                  aria-label={`Show directive: ${c}`}
                >
                  {c}
                </button>
              ) : (
                <span key={c} className="fn-run-pill fn-run-pill-created">
                  {c}
                </span>
              ),
            )}
          </div>
        </div>
      ) : null}
      {drill ? (
        <RunDrillDown
          records={drillRecords(records, drill, nodeId)}
          filter={drill}
          capped={records.length >= MAX_ACTIONS_PER_NODE}
          onClose={closeDrill}
          closeBtnRef={closeBtnRef}
        />
      ) : (trace?.length ?? 0) > 0 ? (
        <RunTranscript entries={trace!} actions={records} live={isCurrent} />
      ) : null}
      {!meta && status === "pending" ? (
        <div className="fn-run-inspector-empty">Not executed yet.</div>
      ) : null}
    </div>
  );
}

function Fact({ k, v }: { k: string; v: string }) {
  return (
    <div className="fn-run-fact">
      <span className="fn-run-fact-k">{k}</span>
      <span className="fn-run-fact-v">{v}</span>
    </div>
  );
}

// ── Artifact inspector (flow-visualizer-v3 R2/W2b) ────────────────────────

/** One row of a simplify/v1 audit array, shape not guaranteed — read defensively. */
type AuditRowLike = Record<string, unknown>;

function auditKind(row: AuditRowLike): string {
  const v = row.kind ?? row.action ?? row.status;
  return typeof v === "string" && v ? v : "row";
}
function auditText(row: AuditRowLike): string {
  const v = row.text ?? row.summary ?? row.item ?? row.name;
  return typeof v === "string" && v ? v : JSON.stringify(row);
}
function auditWhy(row: AuditRowLike): string | undefined {
  const v = row.why ?? row.reason;
  return typeof v === "string" && v ? v : undefined;
}

interface AuditLadder {
  /** Per-layer item counts, in declaration order — the "ladder" rungs. */
  layers: Array<{ label: string; count: number }>;
  auditRows: AuditRowLike[];
}

/**
 * Pulls a simplify/v1 ladder out of a parsed artifact, or `undefined` if the JSON doesn't carry
 * one (codebase-map §5: no fixed schema exists yet, so both `truths` shapes — an array of layers,
 * or an object keyed by layer name — are read, and anything else quietly yields no ladder rather
 * than guessing wrong).
 */
function extractLadder(json: unknown): AuditLadder | undefined {
  if (!json || typeof json !== "object") {
    return undefined;
  }
  const obj = json as Record<string, unknown>;
  const layers: Array<{ label: string; count: number }> = [];
  const truths = obj.truths;
  if (Array.isArray(truths)) {
    truths.forEach((layer, i) => {
      const layerObj =
        layer && typeof layer === "object" ? (layer as Record<string, unknown>) : undefined;
      const label = typeof layerObj?.name === "string" ? layerObj.name : `Layer ${i}`;
      const items = layerObj?.items;
      const count = Array.isArray(items) ? items.length : Array.isArray(layer) ? layer.length : 1;
      layers.push({ label, count });
    });
  } else if (truths && typeof truths === "object") {
    for (const [label, value] of Object.entries(truths as Record<string, unknown>)) {
      layers.push({ label, count: Array.isArray(value) ? value.length : 1 });
    }
  }
  const auditRaw = obj.audit;
  const auditRows = Array.isArray(auditRaw)
    ? auditRaw.filter((r): r is AuditRowLike => !!r && typeof r === "object")
    : [];
  if (layers.length === 0 && auditRows.length === 0) {
    return undefined;
  }
  return { layers, auditRows };
}

/** Per-layer counts + the audit rows verbatim (kept/merged/dropped-with-why), R2's second ask. */
function ArtifactAuditLadder({ ladder }: { ladder: AuditLadder }) {
  return (
    <>
      {ladder.layers.length > 0 ? (
        <div className="fn-run-pill-group">
          <div className="fn-run-pill-label">Layers</div>
          <div className="fn-run-pills">
            {ladder.layers.map((l, i) => (
              <span key={i} className="fn-run-pill" title={l.label}>
                {l.label}
                <span className="fn-run-pill-count">×{l.count}</span>
              </span>
            ))}
          </div>
        </div>
      ) : null}
      {ladder.auditRows.length > 0 ? (
        <div className="fn-run-pill-group">
          <div className="fn-run-pill-label">Audit</div>
          <div className="fn-artifact-audit-rows">
            {ladder.auditRows.map((row, i) => (
              <div key={i} className={`fn-artifact-audit-row fn-artifact-audit-${auditKind(row)}`}>
                <span className="fn-artifact-audit-kind">{auditKind(row)}</span>
                <span className="fn-artifact-audit-text">{auditText(row)}</span>
                {auditWhy(row) ? (
                  <span className="fn-artifact-audit-why">{auditWhy(row)}</span>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </>
  );
}

/**
 * Inspector for a selected artifact node: identity (path/bytes/mediaType/rows), producer/consumers,
 * the `unreadBy` warning (Q3), an Open action (reuses F15's `onOpenArtifact`), and — for small JSON
 * artifacts — the simplify/v1 ladder read via `onReadArtifact`. Unparseable/oversized/non-JSON
 * always falls back to just path + Open, never an error wall (the host collapses every decline
 * reason to one `unavailable` status for exactly this reason).
 */
function ArtifactInspector({
  data,
  producerLabel,
  consumerLabels,
  onOpenArtifact,
  onReadArtifact,
  content,
  onResizeStart,
}: {
  data: ArtifactNodeData;
  producerLabel?: string;
  consumerLabels: string[];
  onOpenArtifact?: (path: string) => void;
  onReadArtifact?: (path: string) => void;
  content?: ArtifactContentEntry;
  onResizeStart?: (e: React.PointerEvent<HTMLDivElement>) => void;
}) {
  // Any text-ish artifact renders inline now (flow-shared-context: content-on-click) — the list
  // mirrors the host's TEXT_ARTIFACT_RE; binary stays path + Open.
  const isTextish = /\.(json|jsonl|md|txt|csv|log|ya?ml|xml|html?)$/i.test(data.path);
  // Kick off the read once per artifact path — content is undefined until a response (or a
  // previous request) exists, so this fires exactly once per newly-selected artifact.
  React.useEffect(() => {
    if (isTextish && !content && onReadArtifact) {
      onReadArtifact(data.path);
    }
  }, [data.path, isTextish, content, onReadArtifact]);

  const ladder = content?.status === "loaded" ? extractLadder(content.json) : undefined;
  const loaded = content?.status === "loaded" ? content : undefined;
  const prettyJson =
    !ladder && loaded && loaded.json !== undefined
      ? JSON.stringify(loaded.json, null, 2)
      : undefined;

  return (
    <div className="fn-panel fn-run-inspector">
      {onResizeStart ? (
        <div
          className="fn-run-panel-resize-handle"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize inspector panel"
          onPointerDown={onResizeStart}
        />
      ) : null}
      <div className="fn-panel-title" title={data.path}>
        {baseName(data.path)}
      </div>
      <div className="fn-run-inspector-sub">{data.path}</div>
      <div className="fn-run-facts">
        <Fact k="Bytes" v={fmtBytes(data.bytes)} />
        <Fact k="Media type" v={data.mediaType || "—"} />
        {data.rows != null ? <Fact k="Rows" v={data.rows.toLocaleString()} /> : null}
        {producerLabel ? <Fact k="Producer" v={producerLabel} /> : null}
        {consumerLabels.length > 0 ? <Fact k="Consumers" v={consumerLabels.join(", ")} /> : null}
      </div>
      {data.unreadBy.length > 0 ? (
        <div className="fn-artifact-unread-warning" role="alert">
          Handed to {data.unreadBy.join(", ")} and never read.
        </div>
      ) : null}
      {onOpenArtifact ? (
        <button
          type="button"
          className="fn-run-pill fn-run-pill-btn fn-artifact-open-btn"
          onClick={() => onOpenArtifact(data.path)}
        >
          Open
        </button>
      ) : null}
      {loaded?.remote ? (
        <div className="fn-artifact-remote-note">
          From the KnowIt mirror (not on this machine)
          {loaded.version != null ? ` · v${loaded.version}` : ""}
          {loaded.producedBy ? ` · uploaded by ${loaded.producedBy}` : ""}
        </div>
      ) : null}
      {ladder ? (
        <ArtifactAuditLadder ladder={ladder} />
      ) : prettyJson !== undefined ? (
        <pre className="fn-artifact-content">{prettyJson}</pre>
      ) : loaded?.text !== undefined ? (
        <pre className="fn-artifact-content">{loaded.text}</pre>
      ) : content?.status === "loading" ? (
        <div className="fn-run-inspector-empty">Reading…</div>
      ) : null}
    </div>
  );
}

/** Label for a canvas node, falling back to its raw id when it (or its data) can't be found. */
function nodeLabel(nodes: Node[], id: string): string {
  const n = nodes.find((x) => x.id === id);
  return n ? String((n.data as { label?: unknown }).label ?? id) : id;
}

/** Producer/consumer node ids for an artifact, read off the edges `artifactsToRf` drew for it. */
function artifactLinks(
  edges: Edge[],
  artifactId: string,
): { producer?: string; consumers: string[] } {
  let producer: string | undefined;
  const consumers: string[] = [];
  for (const e of edges) {
    const cls = String(e.className || "");
    if (e.target === artifactId && cls.includes("fn-artifact-edge-produced")) {
      producer = e.source;
    } else if (e.source === artifactId && cls.includes("fn-artifact-edge-consumed")) {
      consumers.push(e.target);
    }
  }
  return { producer, consumers };
}

// ── Header strip (WU-4, FR-6.1) ───────────────────────────────────────────
/** Run-wide status rolled up from every node's individual {@link NodeRunStatus}. */
type RunOverallStatus = "idle" | "running" | "completed" | "failed" | "awaiting-approval";

const RUN_STATUS_LABEL: Record<RunOverallStatus, string> = {
  idle: "not started",
  running: "running",
  completed: "completed",
  failed: "failed",
  "awaiting-approval": "awaiting approval",
};

/**
 * Roll every node's status into one run-wide status for the header (FR-6.1).
 *
 * Rule (deterministic, terminal-signal based — NOT a count against every
 * declared node): a branching flow only ever posts statuses for the nodes it
 * actually reaches, so untaken branches would leave a "completed count ≥
 * declared node count" comparison permanently short and the header stuck on
 * "running" forever. Instead:
 *   1. No statuses at all yet → "idle".
 *   2. Anything still active — a node "running", a node "awaiting-approval",
 *      or the designated `currentNodeId` not having posted a status yet
 *      (a hand-off in flight) — the run is not over: "running"/"awaiting-
 *      approval" (running takes precedence, since approval can't be pending
 *      while something is actively executing).
 *   3. Otherwise every reached node is done: "failed" if any of them failed,
 *      else "completed".
 * This also keeps the header honest mid-run: as long as anything is active,
 * step 2 wins regardless of how many of the flow's declared nodes were ever
 * reached.
 */
function deriveRunStatus(
  statuses: Record<string, NodeRunStatus>,
  currentIds: string[],
): RunOverallStatus {
  const vals = Object.values(statuses);
  if (vals.length === 0) {
    return "idle";
  }
  // Set-shaped (Wave 2 fork/join): ANY current branch node without a posted
  // status is a hand-off in flight, so the run is still active.
  const currentPending = currentIds.some((id) => (statuses[id] ?? "pending") === "pending");
  if (vals.some((s) => s === "running") || currentPending) {
    return "running";
  }
  if (vals.some((s) => s === "awaiting-approval")) {
    return "awaiting-approval";
  }
  return vals.some((s) => s === "failed") ? "failed" : "completed";
}

/**
 * Run summary header (FR-6.1): flow name, rolled-up status, nodes done/total,
 * elapsed, run token total, and the bubble/log toggles. Sits above the canvas
 * in `.fn-run-shell`; every control is a native `<button>` (keyboard-reachable,
 * FR-6.1's own a11y note) with `aria-pressed` reflecting its toggle state.
 */
function RunHeader({
  flow,
  runStatus,
  doneNodes,
  totalNodes,
  elapsedMs,
  tokenTotals,
  showBubble,
  onToggleBubble,
  showLog,
  onToggleLog,
  logBtnRef,
  compact,
  showGraphs,
  onToggleGraphs,
}: {
  flow: FlowDefinition;
  runStatus: RunOverallStatus;
  doneNodes: number;
  totalNodes: number;
  elapsedMs: number | undefined;
  tokenTotals: ReturnType<typeof sumRunTokens>;
  showBubble: boolean;
  onToggleBubble: () => void;
  showLog: boolean;
  onToggleLog: () => void;
  /** Ref to the Log toggle button — refocused when Esc closes the log (a11y). */
  logBtnRef?: React.Ref<HTMLButtonElement>;
  /** Compact mode (FR-5.1): wraps instead of the wide layout's horizontal
   * scroll, and grows the toggle buttons to the ≥32px touch target (FR-5.3).
   * Purely additive — omitting it keeps the wide layout pixel-unchanged (NF-1). */
  compact?: boolean;
  showGraphs?: boolean;
  onToggleGraphs?: () => void;
}) {
  return (
    <div
      className={`fn-run-header${compact ? " fn-run-compact-header-bar" : ""}`}
      role="region"
      aria-label={`Run summary: ${flow.name}, ${RUN_STATUS_LABEL[runStatus]}, ${doneNodes} of ${totalNodes} nodes done`}
    >
      <span className="fn-run-header-title" title={flow.name}>
        {flow.name}
      </span>
      <span className={`fn-run-header-status fn-run-header-status-${runStatus}`}>
        {RUN_STATUS_LABEL[runStatus]}
      </span>
      <span className="fn-run-header-stat">
        {doneNodes}/{totalNodes} nodes
      </span>
      {elapsedMs != null ? (
        <span className="fn-run-header-stat">{fmtDuration(elapsedMs)}</span>
      ) : null}
      {tokenTotals ? (
        <span className="fn-run-header-stat">
          {tokenTotals.total.toLocaleString()} tok
          <span className="fn-run-header-stat-sub">
            {" "}
            ({tokenTotals.input.toLocaleString()} in / {tokenTotals.output.toLocaleString()} out)
          </span>
        </span>
      ) : null}
      <span className="fn-run-header-spacer" />
      <button
        type="button"
        className={`fn-run-header-btn${showBubble ? " active" : ""}`}
        onClick={onToggleBubble}
        aria-pressed={showBubble}
        aria-label={showBubble ? "Hide live activity bubble" : "Show live activity bubble"}
        title={showBubble ? "Hide live activity bubble" : "Show live activity bubble"}
      >
        Bubble
      </button>
      {onToggleGraphs ? (
        <button
          type="button"
          className={`fn-run-header-btn${showGraphs ? " active" : ""}`}
          onClick={onToggleGraphs}
          aria-pressed={!!showGraphs}
          aria-label={showGraphs ? "Hide run graphs, show graph canvas" : "Show run graphs"}
          title={showGraphs ? "Back to the canvas" : "Tokens, duration and turns over the run"}
        >
          Graphs
        </button>
      ) : null}
      <button
        type="button"
        ref={logBtnRef}
        className={`fn-run-header-btn${showLog ? " active" : ""}`}
        onClick={onToggleLog}
        aria-pressed={showLog}
        aria-label={showLog ? "Hide actions log, show graph" : "Show actions log"}
        title={showLog ? "Back to graph (Esc)" : "Show actions log"}
      >
        Log
      </button>
    </div>
  );
}

/**
 * If `id` names a forEach declaration node or child template with no meta of its own (see
 * {@link ForEachChildLayer.childrenByAnchor}) but has executed/live children, redirect to one of
 * them — the running one if any, else the most recently started — so the inspector never reports
 * "Not executed yet" about a node whose batches actually ran (flow-visualizer-v3 R1).
 */
function redirectToExecutedChild(
  id: string | null,
  statuses: Record<string, NodeRunStatus>,
  metas: Record<string, NodeRunMeta>,
  childrenByAnchor: Map<string, string[]>,
): string | null {
  if (!id || metas[id]) {
    return id;
  }
  const children = childrenByAnchor.get(id);
  if (!children || children.length === 0) {
    return id;
  }
  const running = children.find((c) => statuses[c] === "running");
  if (running) {
    return running;
  }
  let best: string | null = null;
  let bestAt = -1;
  for (const c of children) {
    const at = metas[c]?.startedAt ?? -1;
    if (at >= bestAt) {
      bestAt = at;
      best = c;
    }
  }
  return best ?? children[children.length - 1];
}

/**
 * Read-only flow graph that visualises a run in progress: each node is coloured
 * by its status and the active node glows. Reuses the editor's transform +
 * floating edges so it looks identical to the editor, minus all editing. Feed
 * it `statuses` / `currentNodeId` from your run event stream.
 *
 * When `metas` is provided, a right-hand inspector shows the selected node's
 * execution details. By default the inspector FOLLOWS the currently-running
 * node (the "current task" view); clicking a node pins the inspector to it,
 * clicking the canvas background resumes following the run.
 *
 * Above the canvas, a header strip (FR-6.1) rolls the whole run up into one
 * line — flow name, status, nodes done/total, elapsed, run tokens — and hosts
 * the bubble/log toggles. The "Log" toggle swaps the canvas (+ inspector) for
 * the full-width {@link RunLog} table; one more click restores the graph.
 */
export function FlowRunView({
  flow,
  statuses = {},
  currentNodeId = null,
  currentNodeIds = null,
  metas = {},
  takenEdges = [],
  traces = {},
  actions = {},
  debug = null,
  approval = null,
  onDebugVerb,
  onToggleBreakpoint,
  onApprovalDecision,
  onOpenArtifact,
  graphKinds,
  pillKinds,
  panelWidth,
  onPanelWidthChange,
  onReadArtifact,
  artifactContents = {},
  artifactVersions,
  childRuns = {},
  tiles = {},
  executors = {},
  queuedReasons = {},
  onOpenTile,
  attempts,
  defaultLayout,
}: FlowRunViewProps) {
  // Wave 2 fork/join: normalize the scalar and set forms into ONE ordered list.
  // `current` is set membership from here on; the first entry is the "primary"
  // current node (inspector-follow, bubble fallback) so single-branch behaviour
  // is unchanged.
  const currentIds = React.useMemo<string[]>(
    () =>
      currentNodeIds && currentNodeIds.length > 0
        ? currentNodeIds
        : currentNodeId
          ? [currentNodeId]
          : [],
    [currentNodeIds, currentNodeId],
  );
  const currentKey = currentIds.join("|");
  const currentSet = React.useMemo(
    () => new Set(currentIds),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentKey],
  );
  const primaryCurrentId = currentIds.length > 0 ? currentIds[0] : null;
  // null = follow the running node; a nodeId = user pinned that node.
  const [pinnedNodeId, setPinnedNodeId] = React.useState<string | null>(null);
  // Live bubble visibility (FR-3.2): default on, plain component state — the
  // host may persist it, but nothing here requires that.
  const [showBubble, setShowBubble] = React.useState(true);
  // Actions log pane (WU-4, FR-4.1): off by default; swaps the canvas area for
  // the full-width RunLog table until toggled back off (one click each way).
  const [showLog, setShowLog] = React.useState(false);
  const logBtnRef = React.useRef<HTMLButtonElement | null>(null);
  // Esc closes the log pane, same contract as the inspector drill-down
  // (FR-2.3, FR-4.6) — refocus the toggle so keyboard users don't lose place.
  React.useEffect(() => {
    if (!showLog) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setShowLog(false);
        window.requestAnimationFrame(() => logBtnRef.current?.focus());
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showLog]);

  // Inspector/paused-card sidebar width (flow-visualizer-v3 R3). Local state so a drag renders at
  // 60fps without waiting on the host round-trip; synced FROM `panelWidth` whenever the prop
  // changes (host push of a persisted value), but the drag itself only calls `onPanelWidthChange`
  // on release — dragging is local-only until then, matching how `pinnedNodeId`/`showBubble` above
  // are local UI state the host doesn't need mid-gesture.
  const [panelWidthState, setPanelWidthState] = React.useState(() =>
    clampPanelWidth(panelWidth ?? RUN_PANEL_DEFAULT_WIDTH),
  );
  React.useEffect(() => {
    if (panelWidth != null) {
      setPanelWidthState(clampPanelWidth(panelWidth));
    }
  }, [panelWidth]);
  // Handle sits on `.fn-run-inspector`'s LEFT edge (styles.css), so dragging left (clientX
  // decreasing) must WIDEN the panel — hence `startX - clientX`, not the other way round.
  const onPanelResizeStart = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = panelWidthState;
      let finalWidth = startWidth;
      const onMove = (ev: PointerEvent) => {
        finalWidth = clampPanelWidth(startWidth + (startX - ev.clientX));
        setPanelWidthState(finalWidth);
      };
      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        onPanelWidthChange?.(finalWidth);
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [panelWidthState, onPanelWidthChange],
  );

  // Compact layout (WU-5, FR-5.1): measured off the shell's OWN box via
  // ResizeObserver — container-based, not a viewport media query — so a
  // narrow host panel (VS Code side panel, embedded column) triggers it
  // regardless of the outer window width.
  const shellRef = React.useRef<HTMLDivElement | null>(null);
  const [compact, setCompact] = React.useState(false);
  React.useEffect(() => {
    const el = shellRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      return;
    }
    const ro = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width == null) {
        return;
      }
      setCompact(width < COMPACT_BREAKPOINT_PX);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // Compact's thumbnail↔full-graph toggle (FR-5.1); collapses back whenever
  // the layout widens back out of compact so it doesn't linger stale.
  // Graphs pane (W3). Swaps the canvas like the Log pane does, rather than stealing space from
  // it — on a paused run the graph and the numbers are read at different moments.
  const [showGraphs, setShowGraphs] = React.useState(false);
  const [compactGraphExpanded, setCompactGraphExpanded] = React.useState(false);
  const compactThumbBtnRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    if (!compact) {
      setCompactGraphExpanded(false);
    }
  }, [compact]);
  // Esc collapses the expanded compact graph, same contract as the
  // drill-down/Log bindings above (its "Back to node view (Esc)" title
  // promises this). Bound only while the close button is actually the
  // topmost visible layer: `showLog` isn't reset when it toggles on, so a
  // stale `compactGraphExpanded` could otherwise sit "expanded" underneath
  // the log pane and steal the same Escape press meant to close the log.
  const compactGraphCloseVisible = compact && !showLog && compactGraphExpanded;
  React.useEffect(() => {
    if (!compactGraphCloseVisible) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setCompactGraphExpanded(false);
        window.requestAnimationFrame(() => compactThumbBtnRef.current?.focus());
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [compactGraphCloseVisible]);

  // Build + position once per flow identity. Respect the flow's saved layout;
  // if it has none, fall back to the diagonal auto-layout.
  const base = React.useMemo(() => {
    const { nodes, edges } = flowToRf(flow);
    const hasLayout = flow.layout && Object.keys(flow.layout).length > 0;
    return { nodes: hasLayout ? nodes : autoLayout(nodes, edges, "DIAG"), edges };
  }, [flow]);

  // R1 (flow-visualizer-v3 W1): forEach children the run actually executed, materialized as real
  // canvas nodes. `base` alone never draws them — flowToRf only ever sees the DEFINITION array,
  // which has just the child template — so this is recomputed off the observed `<template>#<k>`
  // ids in `statuses`/`metas` instead. Keyed by the SORTED, JOINED id set (not the maps
  // themselves) so a running child's meta ticking every few seconds doesn't reshuffle the layout
  // of children that already landed — only a genuinely NEW child id does that.
  const observedChildKey = React.useMemo(() => {
    const ids = new Set<string>();
    for (const id of Object.keys(statuses)) {
      ids.add(id);
    }
    for (const id of Object.keys(metas)) {
      ids.add(id);
    }
    return [...ids].sort().join("|");
  }, [statuses, metas]);
  const childLayer = React.useMemo(
    () =>
      forEachChildrenToRf(flow, observedChildKey ? observedChildKey.split("|") : [], base.nodes),
    [flow, base.nodes, observedChildKey],
  );
  // R2/W2b: artifact nodes, one per distinct path any node's meta carries. Recomputed straight off
  // `metas`/`statuses` (not `observedChildKey`) since artifacts don't share the forEach child-batch
  // reshuffle concern — `artifactsToRf` already positions by producer, not by insertion order.
  const artifactLayer = React.useMemo(
    () => artifactsToRf(flow, metas, statuses, artifactVersions),
    [flow, metas, statuses, artifactVersions],
  );
  const combinedBaseNodes = React.useMemo(() => {
    let out = base.nodes;
    if (childLayer.nodes.length > 0) out = [...out, ...childLayer.nodes];
    if (artifactLayer.nodes.length > 0) out = [...out, ...artifactLayer.nodes];
    return out;
  }, [base.nodes, childLayer.nodes, artifactLayer.nodes]);
  const combinedBaseEdges = React.useMemo(() => {
    let out = base.edges;
    if (childLayer.edges.length > 0) out = [...out, ...childLayer.edges];
    if (artifactLayer.edges.length > 0) out = [...out, ...artifactLayer.edges];
    return out;
  }, [base.edges, childLayer.edges, artifactLayer.edges]);

  // Visualizer v4 — AUTO-ARRANGE, including MID-FLOW: whenever the node TOPOLOGY changes (a
  // child materializes, an artifact appears), dagre re-lays the whole combined graph so the work
  // always reads coherently. Keyed by the sorted id signature, so a running node's meta ticking
  // does not re-run layout; positions are cached per topology. That is the "classic" layout now;
  // the default is the CASCADE (owner, 2026-10-02), and "saved" is the flow's own layout — the
  // old auto-arrange OFF.
  const hasSavedLayout = !!flow.layout && Object.keys(flow.layout).length > 0;
  const [layoutMode, setLayoutModeState] = React.useState<RunLayoutMode>(
    () => defaultLayout ?? readStoredLayout() ?? "cascade",
  );
  const setLayoutMode = React.useCallback((mode: RunLayoutMode) => {
    setLayoutModeState(mode);
    writeStoredLayout(mode);
  }, []);
  // A remembered "saved" on a flow that has no saved layout would draw the unarranged fallback.
  const effectiveLayout: RunLayoutMode =
    layoutMode === "saved" && !hasSavedLayout ? "cascade" : layoutMode;
  const topoKey = React.useMemo(
    () =>
      combinedBaseNodes
        .map((x) => x.id)
        .sort()
        .join("|"),
    [combinedBaseNodes],
  );
  const layoutCacheRef = React.useRef<{ key: string; pos: Map<string, { x: number; y: number }> }>({
    key: "",
    pos: new Map(),
  });
  // Cascade (cascade.ts): cached under `cascadeKey`, which reads ids and edge endpoints only — a
  // status poll, a meta tick or a streamed transcript chunk can never move a pill.
  const cascadeInput = React.useMemo(
    () => ({
      nodes: combinedBaseNodes.map((x) => ({ id: x.id, satellite: x.type === "artifactNode" })),
      edges: combinedBaseEdges.map((e) => ({ id: e.id, source: e.source, target: e.target })),
    }),
    [combinedBaseNodes, combinedBaseEdges],
  );
  const cascadeSig = React.useMemo(
    () => `${flow.startNodeId ?? ""}#${cascadeKey(cascadeInput.nodes, cascadeInput.edges)}`,
    [flow.startNodeId, cascadeInput],
  );
  const cascadeCacheRef = React.useRef<{ key: string; layout: CascadeLayout | null }>({
    key: "",
    layout: null,
  });
  const cascade = React.useMemo<CascadeLayout | null>(() => {
    if (effectiveLayout !== "cascade") {
      return null;
    }
    if (cascadeCacheRef.current.key !== cascadeSig || !cascadeCacheRef.current.layout) {
      cascadeCacheRef.current = {
        key: cascadeSig,
        layout: cascadeLayout(cascadeInput.nodes, cascadeInput.edges, {
          startId: flow.startNodeId ?? null,
        }),
      };
    }
    return cascadeCacheRef.current.layout;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveLayout, cascadeSig]);
  const arrangedNodes = React.useMemo(() => {
    if (cascade) {
      return combinedBaseNodes.map((x) => {
        const p = cascade.positions.get(x.id);
        return p ? { ...x, position: p } : x;
      });
    }
    if (effectiveLayout !== "classic") {
      return combinedBaseNodes;
    }
    if (layoutCacheRef.current.key !== topoKey) {
      const laid = autoLayout(combinedBaseNodes, combinedBaseEdges, "DIAG");
      layoutCacheRef.current = {
        key: topoKey,
        pos: new Map(laid.map((x) => [x.id, x.position])),
      };
    }
    const pos = layoutCacheRef.current.pos;
    return combinedBaseNodes.map((x) => (pos.has(x.id) ? { ...x, position: pos.get(x.id)! } : x));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cascade, effectiveLayout, topoKey, combinedBaseNodes, combinedBaseEdges]);

  // The full-prompt viewer (cascade pill "full prompt", inspector "Open"). null = closed.
  const [promptNodeId, setPromptNodeId] = React.useState<string | null>(null);
  const showPrompt = React.useCallback((id: string) => setPromptNodeId(id), []);
  const closePrompt = React.useCallback(() => setPromptNodeId(null), []);
  const defById = React.useMemo(
    () => new Map((flow.nodes ?? []).map((n) => [n.nodeId, n] as const)),
    [flow],
  );
  /** A canvas node's definition: its own, or (a forEach child) its template's. */
  const defOf = React.useCallback(
    (n: Node | undefined): NodeDefinition | undefined =>
      n
        ? defById.get(String((n.data as RunNodeData | undefined)?.templateNodeId ?? n.id))
        : undefined,
    [defById],
  );
  const pillPromptFor = React.useCallback(
    (n: Node | undefined): PillPrompt =>
      pillPromptOf(defOf(n), n ? promptAsSent(traces[n.id], metas[n.id]) : undefined),
    [defOf, traces, metas],
  );

  // Subflow children tallies per spawn node (visualizer v4) — the bubble badge + inset header.
  const subflowTallies = React.useMemo(() => {
    const byNode = new Map<
      string,
      { running: number; done: number; failed: number; total: number }
    >();
    for (const c of Object.values(childRuns)) {
      const key = c.parentNodeId ?? "";
      const t = byNode.get(key) ?? { running: 0, done: 0, failed: 0, total: 0 };
      t.total += 1;
      if (c.status === "running") t.running += 1;
      else if (c.status === "failed") t.failed += 1;
      else t.done += 1;
      byNode.set(key, t);
    }
    return byNode;
  }, [childRuns]);
  const anyChildRunning = React.useMemo(
    () => Object.values(childRuns).some((c) => c.status === "running"),
    [childRuns],
  );
  // The inset (the "box in the flow window"): opens itself when a child starts running; stays
  // toggleable by the subflow node or its chip. null = user never touched it (auto behaviour).
  const [insetChoice, setInsetChoice] = React.useState<boolean | null>(null);
  const insetOpen = insetChoice ?? anyChildRunning;
  const hasChildren = Object.keys(childRuns).length > 0;

  const breakpointSet = React.useMemo(
    () => new Set(debug?.breakpoints ?? []),
    [debug?.breakpoints],
  );
  const heldNodeId =
    debug && (debug.status === "paused" || debug.status === "awaiting-approval")
      ? (debug.atNodeId ?? null)
      : null;

  const nodes = React.useMemo<Node[]>(
    () =>
      arrangedNodes.map((n) => {
        const meta = metas[n.id];
        const tally = subflowTallies.get(n.id);
        // Cascade: a flow node becomes a descriptive pill (who, on what, the words it runs on).
        const asPill = !!cascade && n.type === "flowNode";
        const def = asPill ? defOf(n) : undefined;
        const who = asPill ? pillExecutorOf(def, executors[n.id], meta?.thinkerId) : undefined;
        return {
          ...n,
          ...(asPill ? { type: "runPill" } : {}),
          draggable: false,
          selectable: false,
          connectable: false,
          data: {
            ...n.data,
            status: statuses[n.id] || "pending",
            current: currentSet.has(n.id) || (tally ? tally.running > 0 : false),
            nodeId: n.id,
            breakpoint: breakpointSet.has(n.id),
            heldHere: heldNodeId === n.id,
            onToggleBreakpoint: debug && !debug.finished ? onToggleBreakpoint : undefined,
            // v4: the bubble carries the work — measured facts only, never estimates.
            model: meta?.model,
            durationMs: meta?.durationMs,
            startedAt: meta?.startedAt,
            totalTokens: meta?.totalTokens,
            turnsUsed: meta?.budgetTurns ?? meta?.turnsUsed,
            budgetTurns: meta?.turnBudgetHard,
            artifactCount: meta?.artifacts?.length,
            lastEvent: meta?.lastEvent,
            // Weave: what this node IS, and why it is stopped if it is.
            executor: executors[n.id],
            tile: tiles[n.id],
            queuedReason: queuedReasons[n.id],
            onOpenTile,
            ...(tally ? { subflowSummary: tally } : {}),
            ...(asPill
              ? {
                  executorLabel: who!.label,
                  executorRole: who!.role,
                  executorTitle: who!.title,
                  modelLine: pillModelLine(
                    meta?.model ?? (typeof def?.model === "string" ? def.model : undefined),
                    typeof def?.effort === "string" ? def.effort : undefined,
                  ),
                  model: meta?.model ?? (typeof def?.model === "string" ? def.model : undefined),
                  prompt: pillPromptFor(n),
                  attempts: attempts?.[n.id],
                  onShowPrompt: showPrompt,
                }
              : {}),
          },
        };
      }),
    [
      arrangedNodes,
      metas,
      subflowTallies,
      statuses,
      currentSet,
      breakpointSet,
      heldNodeId,
      debug,
      onToggleBreakpoint,
      cascade,
      defOf,
      pillPromptFor,
      executors,
      tiles,
      queuedReasons,
      onOpenTile,
      attempts,
      showPrompt,
    ],
  );

  // Mark traversed edges so FloatingEdge can paint them charts-green. Matched by
  // (source,target) — a taken loop edge lights regardless of its condition text.
  const takenKey = React.useMemo(
    () => new Set(takenEdges.map((e) => `${e.from} ${e.to}`)),
    [takenEdges],
  );
  const edges = React.useMemo<Edge[]>(() => {
    const pillIds = cascade
      ? new Set(combinedBaseNodes.filter((x) => x.type === "flowNode").map((x) => x.id))
      : null;
    return combinedBaseEdges.map((e) => {
      const taken = takenKey.has(`${e.source} ${e.target}`);
      if (pillIds && pillIds.has(e.source) && pillIds.has(e.target)) {
        // Cascade: pill → pill edges run right; a loop back is a dashed arc under the graph.
        const back = cascade!.backEdges.get(e.id);
        const label = shortCondition(e.label);
        return {
          ...e,
          type: "cascade",
          animated: false,
          label: label || undefined,
          markerEnd: { type: MarkerType.ArrowClosed, width: 14, height: 14 },
          data: {
            ...(e.data ?? {}),
            ...(taken ? { taken: true } : {}),
            ...(back ? { back: true, floorY: back.floorY, lane: back.lane } : {}),
          },
          className: [e.className, taken ? "taken" : "", back ? "fn-back-edge" : ""]
            .filter(Boolean)
            .join(" "),
        };
      }
      return taken ? { ...e, data: { ...(e.data ?? {}), taken: true }, className: "taken" } : e;
    });
  }, [combinedBaseEdges, combinedBaseNodes, takenKey, cascade]);


  // Follow the run: pan/zoom to the active node whenever it advances, so it can't
  // scroll off-canvas on a long flow. maxZoom keeps a single node from filling the
  // whole view; padding keeps its neighbours visible for context.
  // Wave 2 fork/join softening: with >1 current node, chasing one branch would
  // yank the viewport around every round — fit-to-selection over ALL current
  // nodes instead, at a gentler zoom.
  const rfRef = React.useRef<ReactFlowInstance | null>(null);
  React.useEffect(() => {
    if (currentIds.length === 0 || !rfRef.current) {
      return;
    }
    const multi = currentIds.length > 1;
    const raf = window.requestAnimationFrame(() => {
      rfRef.current?.fitView({
        nodes: currentIds.map((id) => ({ id })),
        duration: 400,
        maxZoom: multi ? 1.0 : 1.4,
        padding: multi ? 0.25 : 0.4,
      });
    });
    return () => window.cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentKey]);
  // Refit when the layout changes — a cascade is far wider than the classic graph.
  React.useEffect(() => {
    const raf = window.requestAnimationFrame(() => rfRef.current?.fitView({ duration: 300 }));
    return () => window.cancelAnimationFrame(raf);
  }, [effectiveLayout]);

  // Which node the inspector shows: the pinned one, else the (primary) running
  // one, else the last node that has any recorded meta (end-of-run summary).
  // R1 (flow-visualizer-v3 W1): if that lands on a forEach declaration/template node with no
  // meta of its own but executed/live children, redirect to one of THEM instead — the raw id
  // never has a "Not executed yet" verdict to give once its batches actually ran.
  const rawInspectedId =
    pinnedNodeId ?? primaryCurrentId ?? lastMetaNodeId(combinedBaseNodes, metas);
  const inspectedId = redirectToExecutedChild(
    rawInspectedId,
    statuses,
    metas,
    childLayer.childrenByAnchor,
  );
  const inspected = inspectedId ? combinedBaseNodes.find((n) => n.id === inspectedId) : undefined;
  // A pinned node opens the inspector even before the run reaches it — its prompt is worth
  // reading before it runs, not only after.
  const showInspector =
    Object.keys(metas).length > 0 || currentIds.length > 0 || pinnedNodeId !== null;
  const promptNode =
    promptNodeId !== null ? combinedBaseNodes.find((n) => n.id === promptNodeId) : undefined;

  // Header rollup (WU-4, FR-6.1). `elapsedMs` needs a live clock while the run
  // is active — completed nodes already carry their own durationMs, so the
  // interval only ticks (and only re-renders) while something is running.
  const anyRunning = Object.values(statuses).some((s) => s === "running");
  const [nowTick, setNowTick] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!anyRunning) {
      return;
    }
    const id = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [anyRunning]);
  // "done/reached" rather than "done/declared" (FR-6.1 fix): `base.nodes`
  // includes every node the flow COULD visit, but a branching run only ever
  // posts statuses for nodes it actually reaches — comparing against every
  // declared node left the counter (and deriveRunStatus, above) unable to
  // reach its denominator whenever a branch went untaken.
  const totalNodes = Object.keys(statuses).length;
  const doneNodes = Object.values(statuses).filter(
    (s) => s === "completed" || s === "failed",
  ).length;
  const runStatus = deriveRunStatus(statuses, currentIds);
  const tokenTotals = React.useMemo(() => sumRunTokens(metas), [metas]);
  const elapsedMs = React.useMemo(() => computeRunElapsedMs(metas, nowTick), [metas, nowTick]);

  // The live bubble (FR-3.1/3.3) anchors to the node actually running — only
  // when its status IS "running" (a stale current node after completion, or
  // one still awaiting-approval, shows no bubble). With parallel branches the
  // bubble follows the FIRST current node that is running (one bubble; the
  // other branches still glow via `current`). Positioned from that node's
  // flow-space x/y (ViewportPortal keeps it pinned through pan/zoom).
  const runningNodeId = currentIds.find((id) => (statuses[id] || "pending") === "running") ?? null;
  const bubbleNode = runningNodeId ? nodes.find((n) => n.id === runningNodeId) : undefined;

  // Compact mode's previous-node strip (FR-5.1) — only meaningful (and only
  // computed) while compact; excludes whichever node the header card is
  // currently showing so "previous" never points at itself.
  const prevNode = compact
    ? previousCompletedNode(combinedBaseNodes, statuses, metas, actions, inspectedId)
    : null;

  // R2/W2b: producer/consumer node ids for the inspected artifact, if it is one — computed once
  // here rather than inline in JSX so a click doesn't walk `edges` three times per render.
  const inspectedArtifactLinks =
    inspected && inspected.type === "artifactNode" ? artifactLinks(edges, inspected.id) : undefined;

  // Debugger keyboard bindings (FR: muscle memory from real debuggers, which is the point of the
  // metaphor). Bound only while a debug session exists, and never while the operator is typing —
  // F5/F10 inside a text field would otherwise fire the run.
  React.useEffect(() => {
    if (!debug || debug.finished || !onDebugVerb) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) {
        return;
      }
      const legal = new Set(debug.legal);
      if (e.key === "F10" && legal.has("step")) {
        e.preventDefault();
        onDebugVerb("step");
      } else if (e.key === "F5" && legal.has("continue")) {
        e.preventDefault();
        onDebugVerb("continue");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [debug, onDebugVerb]);

  // dir="ltr" below is a DELIBERATE opt-out, not an oversight. A node graph is
  // spatial, not textual: mirroring it would move every node, edge and handle for no
  // reader benefit. @xyflow already pins `.react-flow { direction: ltr }` for the
  // canvas itself; this pins the surrounding shell chrome to match, so the editor
  // cannot end up half-mirrored (flex/text-align inheriting rtl from <html> while
  // every physical rule in styles.css stays ltr).
  return (
    <div className="fn-shell fn-run-shell" dir="ltr" ref={shellRef}>
      {debug ? (
        <DebugToolbar
          state={debug}
          onVerb={(verb, arg) => onDebugVerb?.(verb, arg)}
          runToTarget={pinnedNodeId}
          compact={compact}
        />
      ) : null}
      <RunHeader
        flow={flow}
        runStatus={runStatus}
        doneNodes={doneNodes}
        totalNodes={totalNodes}
        elapsedMs={elapsedMs}
        tokenTotals={tokenTotals}
        showBubble={showBubble}
        onToggleBubble={() => setShowBubble((s) => !s)}
        showLog={showLog}
        onToggleLog={() => setShowLog((s) => !s)}
        logBtnRef={logBtnRef}
        compact={compact}
        showGraphs={showGraphs}
        onToggleGraphs={() => setShowGraphs((s) => !s)}
      />
      <div className="fn-body">
        {showGraphs ? (
          <div className="fn-canvas fn-run-graphs-pane">
            <RunGraphs flow={flow} metas={metas} show={graphKinds} />
          </div>
        ) : showLog ? (
          <div className={`fn-canvas fn-run-log-pane${compact ? " fn-run-compact-log" : ""}`}>
            <RunLog flow={flow} actions={actions} metas={metas} nowMs={nowTick} />
          </div>
        ) : compact ? (
          <RunCompact
            nodes={nodes}
            edges={edges}
            graphExpanded={compactGraphExpanded}
            onToggleGraph={() => setCompactGraphExpanded((v) => !v)}
            onNodeClick={(_evt, node) => setPinnedNodeId(node.id)}
            onPaneClick={() => setPinnedNodeId(null)}
            onCanvasInit={(instance) => (rfRef.current = instance)}
            prev={prevNode}
            label={inspected ? String((inspected.data as RunNodeData).label ?? inspected.id) : "—"}
            status={inspectedId ? statuses[inspectedId] || "pending" : "pending"}
            model={inspectedId ? metas[inspectedId]?.model : undefined}
            elapsedMs={inspectedId ? nodeElapsedMs(metas[inspectedId], nowTick) : undefined}
            isLive={!!inspectedId && currentSet.has(inspectedId)}
            trace={inspectedId ? traces[inspectedId] : undefined}
            thumbBtnRef={compactThumbBtnRef}
          />
        ) : (
          <>
            <div className="fn-canvas">
              <div className="fn-canvas-controls">
                <span className="fn-canvas-seg" role="group" aria-label="Graph layout">
                  <button
                    type="button"
                    className={`fn-canvas-chip${effectiveLayout === "cascade" ? " on" : ""}`}
                    aria-pressed={effectiveLayout === "cascade"}
                    onClick={() => setLayoutMode("cascade")}
                    title="Left → right by step depth; each step a pill with who runs it and its prompt"
                  >
                    ⇶ Cascade
                  </button>
                  <button
                    type="button"
                    className={`fn-canvas-chip${effectiveLayout === "classic" ? " on" : ""}`}
                    aria-pressed={effectiveLayout === "classic"}
                    onClick={() => setLayoutMode("classic")}
                    title="The compact graph, re-laid automatically as children and artifacts appear"
                  >
                    ⌗ Classic
                  </button>
                  {hasSavedLayout ? (
                    <button
                      type="button"
                      className={`fn-canvas-chip${effectiveLayout === "saved" ? " on" : ""}`}
                      aria-pressed={effectiveLayout === "saved"}
                      onClick={() => setLayoutMode("saved")}
                      title="The flow's own saved layout"
                    >
                      Saved
                    </button>
                  ) : null}
                </span>
                {interestOfFlow(flow) ? (
                  <span className="fn-canvas-chip fn-interest-chip" title={interestTitle(flow)}>
                    ⟶ {interestOfFlow(flow)}
                  </span>
                ) : null}
                {hasChildren ? (
                  <button
                    type="button"
                    className={`fn-canvas-chip${insetOpen ? " on" : ""}${anyChildRunning ? " live" : ""}`}
                    onClick={() => setInsetChoice(!insetOpen)}
                    title="Open the subflow box (child runs, live)"
                  >
                    {anyChildRunning ? "▶ " : ""}subflows
                  </button>
                ) : null}
              </div>
              <RunCanvas
                nodes={nodes}
                edges={edges}
                onNodeClick={(_evt, node) => {
                  // v4: clicking a SUBFLOW spawn node toggles the box rather than only pinning.
                  if (subflowTallies.has(node.id)) {
                    setInsetChoice(!insetOpen);
                  }
                  setPinnedNodeId(node.id);
                }}
                onPaneClick={() => setPinnedNodeId(null)}
                onInit={(instance) => (rfRef.current = instance)}
                showBubble={showBubble}
                bubbleNode={bubbleNode}
                traces={traces}
                actions={actions}
              />
              {insetOpen && hasChildren ? (
                <SubflowInset childRuns={childRuns} onClose={() => setInsetChoice(false)} />
              ) : null}
              {promptNode ? (
                <PromptViewer
                  key={promptNode.id}
                  label={String((promptNode.data as RunNodeData).label ?? promptNode.id)}
                  executor={pillExecutorOf(
                    defOf(promptNode),
                    executors[promptNode.id],
                    metas[promptNode.id]?.thinkerId,
                  ).label}
                  modelLine={pillModelLine(
                    metas[promptNode.id]?.model ??
                      (typeof defOf(promptNode)?.model === "string"
                        ? (defOf(promptNode)!.model as string)
                        : undefined),
                    typeof defOf(promptNode)?.effort === "string"
                      ? (defOf(promptNode)!.effort as string)
                      : undefined,
                  )}
                  prompt={pillPromptFor(promptNode)}
                  onClose={closePrompt}
                />
              ) : null}
            </div>
            {heldNodeId || (showInspector && inspected) ? (
              <div
                className="fn-run-side"
                style={
                  { ["--fn-panel-width" as string]: `${panelWidthState}px` } as React.CSSProperties
                }
              >
                {/* The held node's payload sits ABOVE the inspector: when a run is stopped, what
                    it is stopped on is the thing you came to look at. */}
                {heldNodeId && debug ? (
                  <PausedNodeCard
                    flow={flow}
                    nodeId={heldNodeId}
                    state={debug}
                    metas={metas}
                    traces={traces}
                    actions={actions}
                    approval={approval}
                    onDecision={onApprovalDecision}
                    onVerb={(verb, arg) => onDebugVerb?.(verb, arg)}
                  />
                ) : null}
                {showInspector && inspected && inspected.type === "artifactNode" ? (
                  <ArtifactInspector
                    data={inspected.data as ArtifactNodeData}
                    producerLabel={
                      inspectedArtifactLinks?.producer != null
                        ? nodeLabel(combinedBaseNodes, inspectedArtifactLinks.producer)
                        : undefined
                    }
                    consumerLabels={(inspectedArtifactLinks?.consumers ?? []).map((id) =>
                      nodeLabel(combinedBaseNodes, id),
                    )}
                    onOpenArtifact={onOpenArtifact}
                    onReadArtifact={onReadArtifact}
                    content={artifactContents[(inspected.data as ArtifactNodeData).path]}
                    onResizeStart={onPanelResizeStart}
                  />
                ) : showInspector && inspected ? (
                  <RunInspector
                    nodeId={inspected.id}
                    label={String((inspected.data as RunNodeData).label ?? inspected.id)}
                    ownerRole={String((inspected.data as RunNodeData).ownerRole ?? "")}
                    status={statuses[inspected.id] || "pending"}
                    meta={metas[inspected.id]}
                    trace={traces[inspected.id]}
                    actions={actions[inspected.id]}
                    isCurrent={currentSet.has(inspected.id)}
                    following={pinnedNodeId === null}
                    node={(flow.nodes ?? []).find(
                      (n) =>
                        n.nodeId ===
                        ((inspected.data as RunNodeData).templateNodeId ?? inspected.id),
                    )}
                    onOpenArtifact={onOpenArtifact}
                    pillKinds={pillKinds}
                    onResizeStart={onPanelResizeStart}
                    prompt={pillPromptFor(inspected)}
                    onOpenPrompt={() => showPrompt(inspected.id)}
                  />
                ) : null}
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

/** The viewer's last layout choice, per browser. Storage can throw (blocked site data) — never fatal. */
const RUN_LAYOUT_STORAGE_KEY = "docit.flowRun.layout";

function readStoredLayout(): RunLayoutMode | null {
  try {
    const v = window.localStorage?.getItem(RUN_LAYOUT_STORAGE_KEY);
    return v === "cascade" || v === "classic" || v === "saved" ? v : null;
  } catch {
    return null;
  }
}

function writeStoredLayout(mode: RunLayoutMode): void {
  try {
    window.localStorage?.setItem(RUN_LAYOUT_STORAGE_KEY, mode);
  } catch {
    // a blocked store just means the choice is not remembered
  }
}

/** Most recently started node that has meta — shown after a run finishes. */
function lastMetaNodeId(nodes: Node[], metas: Record<string, NodeRunMeta>): string | null {
  let best: string | null = null;
  let bestAt = -1;
  for (const n of nodes) {
    const at = metas[n.id]?.startedAt ?? -1;
    if (metas[n.id] && at >= bestAt) {
      best = n.id;
      bestAt = at;
    }
  }
  return best;
}
